import axios from 'axios'
import {
  countryCode,
  getAuthorizationToken,
  getPartnerGuid,
  getRefreshToken,
  getUserGuid
} from './access.es6'
import {
  getEnvironmentInformation,
  ignoreMaxDaysAllowedAfterDischargeOrDeath
} from './utils.es6'

const querystring = require('query-string')

let EnvironmentalInformation = null
let BASE_URL
let CENTRAL_AUTHENTICATION_BASE_URL
let AUTH_BASE_URL
let CUSTOMER_APPLICATION_BASE_URL

EnvironmentalInformation = getEnvironmentInformation()

if (EnvironmentalInformation) {
  BASE_URL = EnvironmentalInformation.servicesURLs.api
  CENTRAL_AUTHENTICATION_BASE_URL =
    EnvironmentalInformation.servicesURLs.centralAuthentication
  AUTH_BASE_URL = EnvironmentalInformation.servicesURLs.authApplication
  CUSTOMER_APPLICATION_BASE_URL =
    EnvironmentalInformation.servicesURLs.customerApplication
} else {
  console.warn(
    `Environment ${window.location.origin} configuarations could not be determined`
  )
}

function addPartnerQuery(config) {
  const url = new URL(config.url)

  const excludedPath = 'api/partners/selected'

  if (url.pathname.includes(excludedPath)) {
    return config
  }

  if (!url.searchParams.has('partner')) {
    url.searchParams.set('partner', getPartnerGuid())
  }

  config.url = url.toString()
  return config
}

export const crudApiInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Content-Language': 'en'
  }
})

const SMS_EXECUTOR_URL = EnvironmentalInformation
  ? EnvironmentalInformation.servicesURLs.sms
  : 'https://sms.staging.aspin-inclusivity.com'

const smsExecutorInstance = axios.create({
  baseURL: SMS_EXECUTOR_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})

crudApiInstance.interceptors.request.use(config => addPartnerQuery(config))

const bulkSmsInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Content-Language': 'en'
  }
})
bulkSmsInstance.interceptors.request.use(config => addPartnerQuery(config))

const reportsInstance = axios.create({
  baseURL: BASE_URL,
  responseType: 'arraybuffer'
})
reportsInstance.interceptors.request.use(config => addPartnerQuery(config))

const loyaltyInstance = axios.create({
  baseURL: `${BASE_URL}api/loyaltyrenewal`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Content-Language': 'en',
    'Access-Control-Allow-Origin': '*'
  }
})
loyaltyInstance.interceptors.request.use(config => addPartnerQuery(config))

const centralAuthenticationInstance = axios.create({
  baseURL: CENTRAL_AUTHENTICATION_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})

const customerApplicationInstance = axios.create({
  baseURL: CUSTOMER_APPLICATION_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})
customerApplicationInstance.interceptors.request.use(config =>
  addPartnerQuery(config)
)

const paymentServiceInstance = axios.create({
  baseURL: BASE_URL,

  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})
paymentServiceInstance.interceptors.request.use(config =>
  addPartnerQuery(config)
)

const authInstance = axios.create({
  baseURL: AUTH_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Content-Language': 'en'
  }
})

export function registerCustomer(args) {
  // TODO: This should be part of the UI (i.e. current partner)
  args.partner_guid = getPartnerGuid()
  args.registration_channel = 'WebClient'
  // add the Authorization header
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/customers/register?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function loginUser(args) {
  // const username = encodeURIComponent(args.username);
  // const password = encodeURIComponent(args.password);
  // TODO check this is the correct way to do it
  const username = args.username
  const password = args.password

  return authInstance.post(
    `oauth/token?grant_type=password&scope=all&username=${username}&password=${password}`,
    querystring.stringify({
      username,
      password,
      grant_type: 'password'
    }),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${btoa(
          `${EnvironmentalInformation.credentials.clientID}:${EnvironmentalInformation.credentials.clientSecret}`
        )}`
      }
    }
  )
}

export function getStarted(args) {
  return crudApiInstance.post(`/public/create_outbound_email`, args.payload)
}

export function logoutUser() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get('api/users/logout', { data: {} })
}

export function getUserInformation() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.post(
    `/auth/realms/${EnvironmentalInformation.credentials.realm}/protocol/openid-connect/userinfo`
  )
}

export function getUser(userId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.post(
    `/auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(userId)}`
  )
}

// Look a single Keycloak user up by its id — services record `created_by` /
// `approved_by` as the token's `sub`, so this is what turns a maker/checker
// guid into a human name.
export function getUserById(userId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.get(
    `/auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(userId)}`
  )
}

export function getUserPartners(args) {
  // TODO: Remove this when done
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post('/api/partners/selected', args)
}

export function refreshSession() {
  const refreshToken = getRefreshToken()
  const urlQueryParams = querystring.stringify({
    refresh_token: refreshToken,
    client_id: EnvironmentalInformation.credentials.clientID,
    client_secret: EnvironmentalInformation.credentials.clientSecret,
    grant_type: 'refresh_token'
  })

  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded'
  }

  return centralAuthenticationInstance.post(
    `/auth/realms/${EnvironmentalInformation.credentials.realm}/protocol/openid-connect/token`,
    urlQueryParams,
    { headers }
  )
}

export function recoverPassword(userName) {
  return crudApiInstance.put(`forgotpassword/${userName}`, { data: {} })
}

export function verifyOtp(pendingAuthToken, otp) {
  return authInstance.post('oauth/token/mfa/verify', {
    pendingAuthToken,
    otp
  })
}

export function resendOtp(pendingAuthToken) {
  return authInstance.post('oauth/token/mfa/resend', {
    pendingAuthToken
  })
}

export function listCustomers() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/customers?partner=${encodeURIComponent(getPartnerGuid())}`,
    {
      data: {}
    }
  )
}

export function purchasePolicy(args) {
  args.channel = 'WebClient'
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/products/buy?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function searchCustomerByMsisdn(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/find?msisdn=${args.msisdn}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&size=${args.size}&page=${args.page}`,
    { data: {} }
  )
}

export function searchCustomerByName(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/find?name=${args.name}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&size=${args.size}&page=${args.page}`,
    { data: {} }
  )
}

export function searchCustomerByNationalId(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/find?national_id=${encodeURIComponent(
      args.national_id
    )}&partner=${encodeURIComponent(getPartnerGuid())}&size=${args.size}&page=${
      args.page
    }`,
    { data: {} }
  )
}

export function searchCustomerByExternalId(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/find?external_identifier=${
      args.external_identifier
    }&partner=${encodeURIComponent(getPartnerGuid())}&size=${args.size}&page=${
      args.page
    }`,
    { data: {} }
  )
}

export function findCustomerForTransfer(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  let params = `partner=${encodeURIComponent(getPartnerGuid())}`
  if (args.msisdn) params += `&msisdn=${args.msisdn}`
  if (args.name) params += `&name=${encodeURIComponent(args.name)}`
  if (args.national_id) {
    params += `&national_id=${encodeURIComponent(args.national_id)}`
  }
  if (args.external_identifier) {
    params += `&external_identifier=${args.external_identifier}`
  }
  params += `&page=${args.page || 1}`
  params += `&size=${args.size || 10}`
  return customerApplicationInstance.get(`api/customers/find?${params}`, {
    data: {}
  })
}

export function switchPolicyCustomer(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/policies/complex/switch_customer?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function searchCustomerByPolicyNumber(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/complex/policy_number?policy_number=${
      args.policy_number
    }&partner=${encodeURIComponent(getPartnerGuid())}`,
    { data: {} }
  )
}

export function submitClaim(args) {
  args.payload.ussd_initiated = false

  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  const params = `?partner=${encodeURIComponent(getPartnerGuid())}`

  if (args.ignoreMaxDaysAllowed) {
    args.payload.ignore_max_days = true
  }
  return crudApiInstance.post(`/api/claims${params}`, args.payload)
}

export function getclaimEnum() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/metadata/claim?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateClaim(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  let params = ''
  if (args.ignoreMaxDays) {
    params = `?${ignoreMaxDaysAllowedAfterDischargeOrDeath}=${args.ignoreMaxDays}`
  }

  args.editor_guid = getUserGuid()

  return crudApiInstance.put(
    `/api/claims/${encodeURIComponent(args.guid)}${params}`,
    args
  )
}

export function revertExpiredClaim(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/claims/revert_expired/${encodeURIComponent(args.guid)}`
  )
}

export function getOpenClaims() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/claims/open?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getPayableClaims() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/claims/payable?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getClosedClaims() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/claims/closed?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getClaimsByStatus(status) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const url = `/api/claims?partner=${encodeURIComponent(
    getPartnerGuid()
  )}&status=${status}`
  return crudApiInstance.get(url)
}

export function getClaimAssessments(claimGuid) {
  console.log('getClaimAssessments', claimGuid)

  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const url = `/api/assessment?partner=${encodeURIComponent(
    getPartnerGuid()
  )}&claim_guid=${encodeURIComponent(claimGuid)}`
  return crudApiInstance.get(url)
}

export function searchClaim(criteria) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  let params = ''
  if (criteria.guid) {
    params += `&guid=${encodeURIComponent(criteria.guid)}`
  }
  if (criteria.status) {
    params += `&status=${criteria.status}`
  }
  if (criteria.type) {
    params += `&type=${criteria.type}`
  }
  if (criteria.page >= 0 && criteria.size) {
    params += `&page=${criteria.page}`
    params += `&size=${criteria.size}`
  }
  const url = `/api/claims?partner=${encodeURIComponent(
    getPartnerGuid()
  )}${params}`
  return crudApiInstance.get(url)
}

export function getSummaryClaims() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/claims/summary?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function deletePolicy(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/policies/${args}?partner=${encodeURIComponent(getPartnerGuid())}`,
    { data: {} }
  )
}

export function getCustomerInfo(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/${args}?partner=${encodeURIComponent(getPartnerGuid())}`,
    { data: {} }
  )
}

export function getCurrentProfile(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.get(
    `auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(guid)}`,
    { data: {} }
  )
}

export function resetPassword(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.put(
    `auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(
      args.guid
    )}/execute-actions-email?lifespan=300`,
    ['UPDATE_PASSWORD']
  )
}

export function updateCurrentProfile(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const userGuid = args.userGuid || args.guid
  return crudApiInstance.put(
    `auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(userGuid)}`,
    args
  )
}
export function updateUserRolesAndPartners(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const id = args.id
  return centralAuthenticationInstance.put(
    `auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(id)}`,
    args
  )
}

export function assignUserRoleGroup(args) {
  console.log('assignUserRoleGroup', args)

  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const userId = args.userId
  const roleId = args.roleId
  return centralAuthenticationInstance.put(
    `auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(userId)}/groups/${encodeURIComponent(roleId)}`
  )
}

export function revokeUserRoleGroup(args) {
  console.log('revokeUserRoleGroup', args)
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const userId = args.userId
  const roleId = args.roleId
  return centralAuthenticationInstance.delete(
    `auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(userId)}/groups/${encodeURIComponent(roleId)}`
  )
}

export function updateCustomer(args) {
  // TODO: This should be part of the UI (i.e. current partner)
  args.partner_guid = getPartnerGuid()
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const customerGuid = args.guid
  return customerApplicationInstance.put(
    `api/customers/${encodeURIComponent(customerGuid)}`,
    args
  )
}

export function disableUser(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const userId = args.id
  return centralAuthenticationInstance.put(
    `auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(userId)}`,
    args
  )
}

export function enableUser(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const userId = args.id
  return centralAuthenticationInstance.put(
    `auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(userId)}`,
    args
  )
}

export function updatePolicy(args) {
  // TODO: This should be part of the UI (i.e. current partner)
  args.partner_guid = getPartnerGuid()
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const policyGuid = args.guid
  return crudApiInstance.put(
    `api/policies/${encodeURIComponent(policyGuid)}`,
    args
  )
}

export function listPaidProducts() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/products?type=paid&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&active_only=${true}`,
    { data: {} }
  )
}

export function getReport(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post('/api/reports', args)
}

export function getUsers(params) {
  const first = (params && params.first) || 0
  const max = (params && params.max) || 10
  const search = (params && params.search) || ''
  const realm = EnvironmentalInformation.credentials.realm
  const partner = getPartnerGuid()
  let url = `/auth/admin/realms/${realm}/users?q=partners:${encodeURIComponent(
    partner
  )}&enabled=true&first=${first}&max=${max}`
  if (search) url += `&search=${encodeURIComponent(search)}`
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.get(url)
}
export function getAgentUsers(params) {
  const first = (params && params.first) || 0
  const max = (params && params.max) || 50
  const search = (params && params.search) || ''
  const realm = EnvironmentalInformation.credentials.realm
  const partner = getPartnerGuid()
  let url = `/auth/admin/realms/${realm}/users?q=partners:${encodeURIComponent(
    partner
  )} isAgent:true&enabled=true&first=${first}&max=${max}`
  if (search) url += `&search=${encodeURIComponent(search)}`
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.get(url)
}

export function getDisabledUsers(params) {
  const first = (params && params.first) || 0
  const max = (params && params.max) || 10
  const search = (params && params.search) || ''
  const realm = EnvironmentalInformation.credentials.realm
  const partner = getPartnerGuid()
  let url = `/auth/admin/realms/${realm}/users?q=partners:${encodeURIComponent(
    partner
  )}&enabled=false&first=${first}&max=${max}`
  if (search) url += `&search=${encodeURIComponent(search)}`
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.get(url)
}

export function getUserAuthActivities(args) {
  const userId = args.userId
  const from = args.fromDate ? args.fromDate.toISOString().split('T')[0] : ''
  const to = args.toDate ? args.toDate.toISOString().split('T')[0] : ''
  const size = args.size || 10
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/audit_events/keycloak/user_events?user_id=${encodeURIComponent(
      userId
    )}&from=${from}&to=${to}&max=${size}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function getAllUsersAuthActivities(args) {
  const from = args.fromDate ? args.fromDate.toISOString().split('T')[0] : ''
  const to = args.toDate ? args.toDate.toISOString().split('T')[0] : ''
  const size = args.size || 10
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/audit_events/keycloak/all_events?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&from=${from}&to=${to}&max=${size}`
  )
}

export function getSmsTemplates(arg) {
  let params = ''
  if (arg.page >= 0 && arg.size) {
    params += `&page=${arg.page}`
    params += `&size=${arg.size}`
  }
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/sms_templates?partner=${encodeURIComponent(
      getPartnerGuid()
    )}${params}`
  )
}

export function getSupportedParameters(partner) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/metadata/sms_template?partner=${encodeURIComponent(partner)}`
  )
}
export function createSmsTemplate(smsTemplate) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(`/api/sms_templates`, smsTemplate)
}

export function updateSmsTemplate(smsTemplate) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/sms_templates/${encodeURIComponent(smsTemplate.guid)}`,
    smsTemplate
  )
}

export function deleteSmsTemplate(smsTemplateGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/sms_templates/${encodeURIComponent(smsTemplateGuid)}`
  )
}

export function createUser(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.post(
    `/auth/admin/realms/${EnvironmentalInformation.credentials.realm}/users`,
    args
  )
}

export function getCountries() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`/api/countries`)
}

export function createCountry(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post('/api/countries', args)
}

export function updateCountry(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(`/api/countries/${args.code}`, args)
}

export function deleteCountry(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(`/api/countries/${args}`)
}

export function getPartners() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get('/api/partners', { data: {} })
}

export function createPartner(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post('/api/partners', args)
}

export function getPartner(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`/api/partners/${args}`, { data: {} })
}

export function getProducts(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  let url = `/api/products?partner=${encodeURIComponent(args.partnerGuid)}`
  if (args.type) {
    url += `&type=${args.type}`
  }

  if (args.activeOnly) {
    url += `&active_only=${args.activeOnly}`
  }

  return crudApiInstance.get(url, { data: {} })
}

export function createProduct(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/products?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function createProductPackage(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // partner is routing-only (query string); the backend PackageDto rejects it
  // as an unknown body field (forbidNonWhitelisted → 400). product_guid stays
  // in the body — PackageDto requires it, even though the controller also
  // re-derives it from the URL param.
  const { partner, ...body } = args
  return crudApiInstance.post(
    `/api/products/packages/${encodeURIComponent(
      args.product_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function getProductPackages(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/packages/${args}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function updateProductPackage(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // package_guid + partner are routing-only (URL path + query); the backend
  // PackageDto rejects them as unknown body fields (forbidNonWhitelisted → 400).
  const { package_guid, partner, ...body } = args
  return crudApiInstance.put(
    `/api/products/packages/${encodeURIComponent(
      package_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}
export function deleteProductPackage(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/products/packages/${encodeURIComponent(
      args.package_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createProductPackageBenefit(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // partner is routing-only (query string); product_guid isn't a
  // PackageBenefitDto field at all (benefits are scoped by package_guid, not
  // product_guid) — both are unknown-property 400s under forbidNonWhitelisted.
  const { partner, product_guid, ...body } = args
  return crudApiInstance.post(
    `/api/products/package_benefits/${encodeURIComponent(
      args.package_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function getProductPackageBenefits(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/package_benefits/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function deleteProductPackageBenefit(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/products/package_benefits/${encodeURIComponent(
      args.benefit_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateProductPackageBenefit(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // benefit_guid + partner + product_guid are routing-only/nonexistent on
  // PackageBenefitDto — see createProductPackageBenefit.
  const { benefit_guid, partner, product_guid, ...body } = args
  return crudApiInstance.put(
    `/api/products/package_benefits/${encodeURIComponent(
      benefit_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function getProductAddons(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/add_ons/${args}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function createProductAddon(args) {
  console.log('createProductAddon args', args)
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // partner is routing-only (query string); AddOnDto rejects it as an unknown
  // body field (forbidNonWhitelisted → 400). product_guid stays — AddOnDto
  // requires it in the body (no URL param carries it for this route).
  const { partner, ...body } = args
  return crudApiInstance.post(
    `/api/products/add_ons?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function updateProductAddon(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // add_on_guid + partner are routing-only; AddOnDto has no add_on_guid field.
  const { add_on_guid, partner, ...body } = args
  return crudApiInstance.put(
    `/api/products/add_ons/${encodeURIComponent(
      add_on_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function deleteProductAddon(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/products/add_ons/${encodeURIComponent(
      args.add_on_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createCashbackConfiguration(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // partner is routing-only; CashbackIndemnityDto rejects it as an unknown
  // body field (forbidNonWhitelisted → 400). product_guid stays — the DTO
  // requires it in the body.
  const { partner, ...body } = args
  return crudApiInstance.post(
    `/api/cashback_indemnity?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function getCashbackConfigurations(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/cashback_indemnity/${args}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}
export function updateCashbackConfiguration(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // UpdateCashbackIndemnityDto is deliberately narrow — only period,
  // period_granularity, enabled, returned_premiums_count, premium_percentage,
  // fixed_premium_amount. cashback_guid/partner are routing-only, and unlike
  // the create DTO it declares no guid, product_guid, or name field either.
  const { cashback_guid, partner, product_guid, name, ...body } = args
  return crudApiInstance.put(
    `/api/cashback_indemnity/${encodeURIComponent(
      cashback_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function deleteCashbackConfiguration(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/cashback_indemnity/${encodeURIComponent(
      args.cashback_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createQuote(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/quotations/create?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function evaluateProductQuote(productGuid, args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/products/${encodeURIComponent(
      productGuid
    )}/evaluations?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function compareQuotations(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/products/quotations/compare?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function createAgeValidationRule(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // partner is routing-only; AgeValidationRuleDto rejects it as an unknown
  // body field (forbidNonWhitelisted → 400). product_guid stays — the DTO
  // requires it in the body.
  const { partner, ...body } = args
  return crudApiInstance.post(
    `/api/products/age_validation_rules/${encodeURIComponent(
      args.product_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function getAgeValidationRules(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/age_validation_rules/${args}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function deleteAgeValidationRule(rule_guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/products/age_validation_rules/${encodeURIComponent(
      rule_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateAgeValidationRule(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // rule_guid + partner are routing-only; AgeValidationRuleDto's own guid
  // field is `guid`, not `rule_guid`. product_guid stays — it's a real field.
  const { rule_guid, partner, ...body } = args
  return crudApiInstance.put(
    `/api/products/age_validation_rules/${encodeURIComponent(
      rule_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function createProductAutoRenewalConfiguration(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // partner is routing-only. product_guid is a declared RenewalDto field but
  // — unlike every other entity in this file — carries no class-validator
  // decorator (only dto-mapper's @include()), so class-validator's whitelist
  // doesn't recognize it either: forbidNonWhitelisted rejects it all the
  // same. Confirmed live against a running instance, not just DTO source.
  const { partner, product_guid, ...body } = args
  return crudApiInstance.post(
    `/api/products/renewals/${encodeURIComponent(
      args.product_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function getProductAutoRenewalConfiguration(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/renewals/${args}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function deleteProductAutoRenewalConfiguration(renewal_guid) {
  console.log('deleteProductAutoRenewalConfiguration', renewal_guid)
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/products/renewals/${encodeURIComponent(
      renewal_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateProductAutoRenewalConfiguration(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // renewal_guid, auto_renewal_guid, and partner are all routing-only;
  // RenewalDto's own guid field is `guid`, not either of those names.
  // product_guid is dropped too — see createProductAutoRenewalConfiguration.
  const {
    renewal_guid,
    auto_renewal_guid,
    partner,
    product_guid,
    ...body
  } = args
  return crudApiInstance.put(
    `/api/products/renewals/${encodeURIComponent(
      renewal_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function changeQuote(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  // Build query string, only including start_date if it has a valid value
  let queryString = `policy_guid=${encodeURIComponent(
    args.policy_guid
  )}&quotation_guid=${encodeURIComponent(
    args.quotation_guid
  )}&customer_guid=${encodeURIComponent(args.customer_guid)}`
  if (args.start_date) {
    queryString += `&start_date=${args.start_date}`
  }
  queryString += `&partner=${encodeURIComponent(getPartnerGuid())}`

  return crudApiInstance.put(
    `/api/policies/complex/change_quotation?${queryString}`,
    args
  )
}

export function updateQuote(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/policies/complex/update_quotation?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&quotation_guid=${encodeURIComponent(
      args.quotation_guid
    )}&customer_guid=${encodeURIComponent(
      args.customer_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function buyPolicy(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/policies/complex/purchase?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function getCustomerPolicies(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/complex/customer?customer_guid=${encodeURIComponent(
      args.guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getCustomerQuotes(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/quotations/customer?customer_guid=${encodeURIComponent(
      args.guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// Approve an amended quotation. On confirm the server attaches the quotation
// to its policy. No request body — the guid and partner identify the target.
export function confirmQuotation(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/quotations/${encodeURIComponent(
      args.guid
    )}/confirm?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// Reject an amended quotation. No request body accepted by the endpoint.
export function rejectQuotation(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/quotations/${encodeURIComponent(
      args.guid
    )}/reject?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// Lists quotations for the Amended Policies dashboard via the filter endpoint.
// The caller passes the filter payload (e.g. { status, partner }) which is sent
// as the POST body; the backend applies the status filter.
export function getQuotationsByStatus(args = {}) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/quotations/filter?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

// Reads a single quotation. Used when an amended quotation records the guid of
// the quotation it supersedes, so the two can be compared field by field.
export function getQuotationByGuid(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/quotations?guid=${encodeURIComponent(
      args.guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getComplexCustomerPremiums(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/complex/premiums?customer_guid=${encodeURIComponent(
      args.guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getComplexPolicyRefunds(policyGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/complex/premiums/policy/refunds?policy_guid=${encodeURIComponent(
      policyGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function removeComplexPremium(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/policies/complex/premiums/remove?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function initiatePremiumTransfer(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/policies/complex/premium-transfer?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function decidePremiumTransfer(transferGuid, decision, rejectionReason) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/policies/complex/premium-transfer/${encodeURIComponent(
      transferGuid
    )}/decide?partner=${encodeURIComponent(getPartnerGuid())}`,
    { decision, rejection_reason: rejectionReason }
  )
}

export function getPremiumTransferRequests(policyGuid, status) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const params = new URLSearchParams({ partner: getPartnerGuid() })
  if (policyGuid) params.set('policy_guid', policyGuid)
  if (status) params.set('status', status)
  return crudApiInstance.get(
    `api/policies/complex/premium-transfer?${params.toString()}`
  )
}

export function getSmsActivities(msisdn) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/sms/customer/${msisdn}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getPolicyByGuid(policy_guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/complex?policy_guid=${encodeURIComponent(
      policy_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function cancelV2Policy(policy_guid, reason) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const reasonParam = reason ? `&reason=${encodeURIComponent(reason)}` : ''
  return crudApiInstance.delete(
    `/api/policies/complex?policy_guid=${encodeURIComponent(
      policy_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}${reasonParam}`
  )
}

export function reinstatePolicy(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/policies/complex/reinstate?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    payload
  )
}

export function updateBeneficiary(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/policies/complex/beneficiary?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function selectPolicies(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/quotations/event?customer_guid=${encodeURIComponent(
      args.customer_guid
    )}&policy_guid=${encodeURIComponent(args.policy_guid)}&event_date=${
      args.event_date
    }&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function initiateClaimdcp(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const partnerGuid = getPartnerGuid()
  const endpoint =
    partnerGuid === 'omk' ||
    partnerGuid === 'omu_flp' ||
    partnerGuid === 'dfcu_ug'
      ? `/api/onboard_claim?partner=${encodeURIComponent(partnerGuid)}`
      : `/api/claims/complex/initiate_claim?partner=${encodeURIComponent(
          partnerGuid
        )}`
  return crudApiInstance.post(endpoint, args)
}

// Old Mutual Malawi (Mthunzi) submits claims directly through the policy-dcp-underwriter engine's
// business-rule flows - the same flows the USSD channel uses via mailroom - rather than the generic
// /api/claims/complex/initiate_claim endpoint every other partner still uses. The engine validates
// policy status, event date window, waiting periods, and benefit coverage synchronously, so a rejected
// claim comes back as a clear, specific error rather than being silently queued and rejected later.
// args.product_code ('mthunzi_death' | 'mthunzi_hcp') tells EngineRouter which of Mthunzi's two
// ClaimInitiation flows to run - both claim types live under one endpoint, same as every other partner
// already routed through /api/onboard_claim (see initiateClaimdcp's omu_flp branch above).
export function initiateMthunziClaim(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    '/api/onboard_claim?partner=old_mutual_malawi',
    args
  )
}

export function getCustomerClaimsDcp(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/claims/complex/customer?customer_guid=${encodeURIComponent(
      args.guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getClaimsDcp(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const partnerGuid = getPartnerGuid()
  let url = `api/claims/complex/statuses?partner=${encodeURIComponent(
    partnerGuid
  )}`
  if (args.tag) {
    url += `&tag=${args.tag}&page=${args.page}&size=${args.size}`
  }

  const result = crudApiInstance.post(url, args)

  const paginatedClaimPartners = ['ngsugguid', 'kymbritam']

  if (!paginatedClaimPartners.includes(partnerGuid)) {
    return result.then(response => ({
      data: response.data.data
    }))
  }
  return result
  // console.log("result", result)
}
export function getClaimsDcpFiltered(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const partnerGuid = getPartnerGuid()
  let url = `api/claims/complex?partner=${encodeURIComponent(partnerGuid)}`

  if (args.tag) url += `&tag=${args.tag}`
  if (args.page) url += `&page=${args.page}`
  if (args.size) url += `&size=${args.size}`
  if (args.customer_guid)
    url += `&customer_guid=${encodeURIComponent(args.customer_guid)}`
  if (args.facility) url += `&facility=${args.facility}`
  if (args.facility_code) url += `&facility_code=${args.facility_code}`
  if (args.type)
    url += `&type=${
      Array.isArray(args.type)
        ? args.type.map(encodeURIComponent).join(',')
        : encodeURIComponent(args.type)
    }`
  if (args.status) url += `&status=${args.status}`
  if (args.start_date) url += `&start_date=${args.start_date}`
  if (args.end_date) url += `&end_date=${args.end_date}`

  const result = crudApiInstance.get(url)

  const paginatedClaimPartners = [
    'ngsugguid',
    'kymbritam',
    'banja',
    'old_mutual_malawi',
    'omk'
  ]

  if (!paginatedClaimPartners.includes(partnerGuid)) {
    return result.then(response => ({
      data: response.data.data
    }))
  }
  return result
}

export function getPoliciesDcp(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/complex/status?status=${
      args.status
    }&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

// Coerce "true"/"false"/boolean into a real boolean for additional_details.
// Note: the legacy `&&` query-param idiom treated the string "false" as truthy
// and shipped it as-is; on /filter the flags must be genuine booleans.
function toBool(v) {
  return v === true || v === 'true'
}

// Build the POST body for /api/policies/complex/filter.
// status, partner, branch, page, size and the date-range params (from/to
// plus date_discriminator, the API's name for the date field the range
// applies to) stay top-level; flags (pep_flagged/aml_flagged) and the
// screening engine's customer_risk_rating nest under additional_details.
//
// maker_check_label / maker_check_status narrow to policies carrying a
// maker-checker stage with that label, in one of those (comma-separated)
// statuses. The server matches "has SOME check like this", not "the check a
// reviewer can act on today", so a caller filtering one stage basket still has
// to refine the result client-side — see makerCheckStage.js.
function buildPolicyFilterBody(args = {}, additionalDetails = {}) {
  const body = { partner: getPartnerGuid() }
  const topLevel = [
    'status',
    'branch',
    'page',
    'size',
    'from_date',
    'to_date',
    'from',
    'to',
    'date_discriminator',
    'policy_number',
    'maker_check_label',
    'maker_check_status',
    'product_code'
  ]
  // page/size must be sent as number strings (e.g. "1"), matching the contract
  // the old query-string transport satisfied implicitly.
  const stringKeys = ['page', 'size']
  topLevel.forEach(function(key) {
    if (args[key] !== undefined && args[key] !== null && args[key] !== '') {
      body[key] = stringKeys.indexOf(key) !== -1 ? String(args[key]) : args[key]
    }
  })
  // customer_risk_rating is written onto additional_details by the screening
  // engine, so callers narrow a flag query to a single rating through it.
  const details = { ...additionalDetails }
  if (args.customer_risk_rating) {
    details.customer_risk_rating = args.customer_risk_rating
  }
  if (Object.keys(details).length > 0) {
    body.additional_details = details
  }
  return body
}

export function getAmlPolicies(args = {}) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const body = buildPolicyFilterBody(args, {
    aml_flagged: toBool(args.aml_flagged)
  })
  return crudApiInstance.post(
    `api/policies/complex/filter?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    body
  )
}

export function getPepPolicies(args = {}) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const body = buildPolicyFilterBody(args, {
    pep_flagged: toBool(args.pep_flagged)
  })
  return crudApiInstance.post(
    `api/policies/complex/filter?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    body
  )
}

export function getIPRSFlaggedPolicies(args = {}) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const body = buildPolicyFilterBody(args, {
    iprs_flagged: toBool(args.iprs_flagged)
  })
  return crudApiInstance.post(
    `api/policies/complex/filter?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    body
  )
}

export function getAmlPoliciesAwaitingReview() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const body = {
    partner: getPartnerGuid(),
    additional_details: {
      aml_action: '',
      aml_flagged: true
    }
  }
  return crudApiInstance.post(
    `api/policies/complex/filter?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    body
  )
}

export function getAmlPoliciesRejected() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const body = {
    partner: getPartnerGuid(),
    status: 'Rejected',
    additional_details: {
      aml_action: 'rejected'
    }
  }
  return crudApiInstance.post(
    `api/policies/complex/filter?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    body
  )
}

export function getAmlPoliciesApproved() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const body = {
    partner: getPartnerGuid(),
    status: 'Unpaid',
    additional_details: {
      aml_action: 'approved'
    }
  }
  return crudApiInstance.post(
    `api/policies/complex/filter?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    body
  )
}

export function getRecentPolicies(args = {}) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const body = buildPolicyFilterBody(
    {
      ...args,
      page: args.page || 1,
      size: args.size || 20
    },
    args.additional_details || {}
  )
  return crudApiInstance.post(
    `api/policies/complex/filter?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    body
  )
}

export function getRecentPayments(args = {}) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const page = args.page || 1
  const size = args.size || 20
  const params = new URLSearchParams({
    partner: getPartnerGuid(),
    page,
    size,
    ...(args.from_date && { from_date: args.from_date }),
    ...(args.to_date && { to_date: args.to_date }),
    ...(args.status && { status: args.status })
  })
  return crudApiInstance.get(`/api/payments/recent?${params.toString()}`)
}

// Payments between two days (effected or created date), one page at a time. Returns { data, page, size, total }.
export function filterPayments(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/payments/filter?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function updatePoliciesDcp(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/policies/complex?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function getPoliciesDcpByBranch(args = {}) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const body = buildPolicyFilterBody({
    ...args,
    page: args.page || 1,
    size: args.size || 10
  })
  return crudApiInstance.post(
    `api/policies/complex/filter?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    body
  )
}

export function getPaymentsDcp(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/payments/status/${encodeURIComponent(getPartnerGuid())}?status=${
      args.status
    }`,
    args
  )
}

export function claimBulkApprove(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  if (['decision_pending', 'approved_pending'].includes(args.status)) {
    return crudApiInstance.post(
      `/api/claims/complex/bulk_approval_selected?partner=${encodeURIComponent(
        getPartnerGuid()
      )}&claim_action=approve`,
      args
    )
  }
  return crudApiInstance.post(
    `/api/claims/complex/bulk_approval_paid_selected?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function createExpense(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/claims/complex/benefit_usages?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function deleteExpense(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/claims/complex/benefit_usages?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&benefit_usage_guid=${encodeURIComponent(
      args.expense_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function editExpense(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  if (getPartnerGuid() === 'old_mutual_malawi') {
    return crudApiInstance.put(
      `api/claims/complex/benefit_usages?claim_guid=${encodeURIComponent(
        args.claim_guid
      )}&benefit_usage_guid=${encodeURIComponent(
        args.benefit_usage_guid
      )}&partner=${encodeURIComponent(getPartnerGuid())}`,
      args
    )
  }
  return crudApiInstance.put(
    `api/claims/complex/expenses?expense_guid=${encodeURIComponent(
      args.expense_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function addClaimDocument(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/claims/complex/documents?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function addBulkPolicyOnboarding(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/bulk-policy-onboarding?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function deleteClaimDocument(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/claims/complex/documents?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&claim_doc_guid=${encodeURIComponent(
      args.claim_doc_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function claimActions(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  if (args.claim_action === 'fail_confirm') {
    return crudApiInstance.post(
      `api/claims/complex/actions/${
        args.claim_action
      }?claim_guid=${encodeURIComponent(
        args.claim_guid
      )}&partner=${encodeURIComponent(getPartnerGuid())}`,
      { failed_confirmation_reason: args.reason }
    )
  }
  if (args.claim_action === 'discard') {
    return crudApiInstance.post(
      `api/claims/complex/actions/${
        args.claim_action
      }?claim_guid=${encodeURIComponent(
        args.claim_guid
      )}&partner=${encodeURIComponent(getPartnerGuid())}`,
      { failed_confirmation_reason: 'discarding it' }
    )
  }

  return crudApiInstance.post(
    `api/claims/complex/actions/${
      args.claim_action
    }?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args.body
  )
}

export function policyActions(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/policies/complex?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args.body
  )
}

export function paymentApproveAction(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/payments/${encodeURIComponent(args.guid)}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args.body
  )
}

export function paymentRejectAction(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/payments/${encodeURIComponent(args.guid)}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args.body
  )
}

export function policyRejectActions(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/policies/complex?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args.body
  )
}

// OMM multi-level (maker-checker) approval: records an Approved/Rejected
// decision on the lowest-sequence Pending check of a complex policy. The
// server derives the approver from the bearer token and enforces separation
// of duties, distinct actor per stage and hard sequencing.
export function decideMakerCheck(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/policies/complex/${encodeURIComponent(
      args.guid
    )}/maker-checks/decisions?partner=${encodeURIComponent(getPartnerGuid())}`,
    args.body
  )
}

export function returnToPriorStage(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/policies/complex/${encodeURIComponent(
      args.guid
    )}/maker-checks/return-to-prior-stage?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args.body
  )
}

export function claimNotes(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/claims/complex?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}
export function addClaimNotes(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/claims/complex/notes?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function deleteClaimNotes(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  console.log('deleteClaimNotes args', args)
  return crudApiInstance.delete(
    `api/claims/complex/notes?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&notes_guid=${encodeURIComponent(
      args.notes_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateClaimNotes(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/claims/complex/notes?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&notes_guid=${encodeURIComponent(
      args.notes_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function claimFacilityNotes(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/claims/complex?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function policyNotes(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  if (!args.type) args.type = 'General'
  return crudApiInstance.post(
    `api/policies/complex/notes?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())} `,
    args
  )
}

export function getClaimProcessingDcp(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/claims/complex/claim?guid=${encodeURIComponent(
      args.guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

/**
 * Issues a claim's settlement voucher and discharge receipt on the first call and
 * returns the same frozen record (numbers, dates, amounts) on every later one.
 */
export function issueClaimSettlementDocuments(claimGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/claims/complex/settlement-documents?guid=${encodeURIComponent(
      claimGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getPolicyProcessingDcp(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/complex/policy_number?policy_number=${
      args.policy_number
    }&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getAudits(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const page = args.page || 0
  const size = args.size || 100
  return crudApiInstance.get(
    `api/audit_events?partner_guid=${encodeURIComponent(
      getPartnerGuid()
    )}&entity_guid=${encodeURIComponent(
      args.entity_guid
    )}&page=${page}&size=${size}`
  )
}

export function getPaymentProcessingDcp(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/payments/${encodeURIComponent(args.guid)}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function getPaymentByPolicyGuid(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/payments?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&policy_guid=${encodeURIComponent(args.guid)}&policy_number=${
      args.policy_number
    }`
  )
}

export function getPreauthDetails(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/claim/preauthorization?guid=${encodeURIComponent(
      args.guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getLous(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/claim/preauthorization/customer?customer_guid=${encodeURIComponent(
      args.customer_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getBenefitUsageBalances(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/claims/complex/benefit_usages/balances?dependant_guid=${encodeURIComponent(
      args.dependant_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateBenefitUsages(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/claims/complex/benefit_usages?claim_guid=${encodeURIComponent(
      args.claim_guid
    )}&benefit_usage_guid=${encodeURIComponent(
      args.benefit_usage_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function createLou(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/claim/preauthorization/create?partner=${encodeURIComponent(
      getPartnerGuid()
    )} `,
    args
  )
}

export function editLou(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/claim/preauthorization?partner=${encodeURIComponent(
      getPartnerGuid()
    )} `,
    args
  )
}

export function deleteLou(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/claim/preauthorization?preauthorization_guid=${encodeURIComponent(
      args.guid
    )}&partner=${encodeURIComponent(getPartnerGuid())} `
  )
}

export function createHospital(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/configs/hospitals?partner=${encodeURIComponent(getPartnerGuid())} `,
    args
  )
}

export function deleteHospital(id) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/configs/hospitals?id=${encodeURIComponent(
      id
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getHospitals() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/configs/hospitals?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function searchHospitals(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/configs/hospitals/search?query=${
      args.query
    }&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createDiagnosis(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/configs/health-diagnosis?partner=${encodeURIComponent(
      getPartnerGuid()
    )} `,
    args
  )
}

export function deleteDiagnosis(id) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/configs/health-diagnosis?id=${encodeURIComponent(
      id
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getDiagnosis() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/configs/health-diagnosis?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function searchDiagnosis(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/configs/health-diagnosis/search?query=${
      args.query
    }&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getPolicyDependents(national_id) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/complex/dependants?national_id=${encodeURIComponent(
      national_id
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function editProduct(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/products/${encodeURIComponent(
      args.product_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function editPremium(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/product_premiums/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function editBenefit(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  let benefit_path_var = ''
  if (args.benefitType === 'cashback') {
    benefit_path_var = 'cashback_indemnity'
  }
  if (args.benefitType === 'fixed') {
    benefit_path_var = 'fixed_indemnity'
  }
  if (args.benefitType === 'variable') {
    benefit_path_var = 'variable_indemnity'
  }
  return crudApiInstance.put(
    `/api/${benefit_path_var}/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function createPremium(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/product_premiums?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function hardDeletePremium(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/product_premiums/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createPremiumConfig(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/extended_membership_premium?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}
export function getFeatureFlags() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/feature-flags?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function addFeatureFlag(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/feature-flags?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function updateFeatureFlag(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/feature-flags/${args.name}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function deleteFeatureFlag(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/feature-flags/${args.name}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function getLookupListTypes() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/configs/lookup-lists/types?partner=${getPartnerGuid()}`
  )
}

export function getLookupList(listType) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/configs/lookup-lists/${listType}?partner=${getPartnerGuid()}`
  )
}

export function addLookupListItem(listType, item) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/configs/lookup-lists/${listType}/items?partner=${getPartnerGuid()}`,
    item
  )
}

export function updateLookupListItem(listType, itemValue, patch) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/configs/lookup-lists/${listType}/items/${encodeURIComponent(
      itemValue
    )}?partner=${getPartnerGuid()}`,
    patch
  )
}

export function deleteLookupListItem(listType, itemValue) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/configs/lookup-lists/${listType}/items/${encodeURIComponent(
      itemValue
    )}?partner=${getPartnerGuid()}`
  )
}

export function updateExtendedConfig(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/membership_configs/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}
export function createExtendedConfig(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/membership_configs?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function getPremiumConfigs(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/extended_membership_premium/${args}/all?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function createFixedIndemnityBenefit(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/fixed_indemnity?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function createVairableIndemnityBenefit(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/variable_indemnity?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function createCashbackIndemnityBenefit(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/cashback_indemnity?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function createBenefit(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post('/api/product_benefits', args)
}

export function hardDeleteBenefit(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/${args.benefitType}/delete/${encodeURIComponent(
      args.benefit.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function downloadDocument(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/documents/${encodeURIComponent(
      args.template_guid
    )}/download?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&entity_guid=${encodeURIComponent(args.entity_guid)}`,
    { responseType: 'blob' }
  )
}

export function emailDocument(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/emails/send_email_event_test?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    {
      partner_guid: getPartnerGuid(),
      event_type: args.event_type,
      entity_guid: args.entity_guid,
      email_recipients: args.email_recipients,
      content: args.content
    }
  )
}

export function getPolicyDocumentTemplate() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/templates?partner=${encodeURIComponent(getPartnerGuid())}&type=LOU`
  )
}

export function getProduct(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/${args}?partner=${encodeURIComponent(getPartnerGuid())}`,
    { data: {} }
  )
}

export function getProductByCode(code) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/${code}/code?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    { data: {} }
  )
}

export function getPartnerConfigs(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/partners/partners_config/${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getPartnerEmailConfigs(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/partners/partners_config/email/partial/${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function getEmailClients() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    '/api/partners/partners_config/email-client-definitions',
    { data: {} }
  )
}

export function getPartnerConfigsClaim(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/partners/partners_config_claim/${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getPartnerConfigsGroup(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/partners/partners_config_group/${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getPartnerConfigsAdminScreens(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/partners/partners_config_admin_screens/${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function updatePartner(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/partners/${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function updatePartnerConfig(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/partners/partners_config/${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function updatePartnerConfigClaim(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/partners/partners_config_claim/${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function updatePartnerConfigEmail(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(`/api/partners/partners_config/email`, args)
}

export function updatePartnerConfigAdminScreens(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/partners/partners_config_admin_screens/${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

// ── SMS Executor Admin API ────────────────────────────────────────────────────

export function getSmsChannels() {
  const guid = getPartnerGuid()
  return smsExecutorInstance.get(`/admin/channels/${encodeURIComponent(guid)}`)
}

export function saveSmsChannel(args) {
  const guid = getPartnerGuid()
  const channelName = args.channel_name || '_default'
  const channel = Object.assign({}, args)
  return smsExecutorInstance.put(
    `/admin/channels/${encodeURIComponent(guid)}/${encodeURIComponent(
      channelName
    )}`,
    channel
  )
}

export function deleteSmsChannel(channelName) {
  const guid = getPartnerGuid()
  return smsExecutorInstance.delete(
    `/admin/channels/${encodeURIComponent(guid)}/${encodeURIComponent(
      channelName
    )}`
  )
}

export function testSmsChannel(args) {
  const guid = getPartnerGuid()
  return smsExecutorInstance.post(
    `/admin/channels/${encodeURIComponent(guid)}/test`,
    args
  )
}

export function upsertAllSmsChannels(args) {
  const guid = getPartnerGuid()
  return smsExecutorInstance.post(
    `/admin/channels/${encodeURIComponent(guid)}`,
    {
      sms_channels: args.sms_channels,
      channel_resolution: args.channel_resolution
    }
  )
}

export function getSmsChannelsByPartnerGuid(guid) {
  return smsExecutorInstance.get(`/admin/channels/${encodeURIComponent(guid)}`)
}

export function updatePartnerConfigGroup(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/partners/partners_config_group/${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function updateGroupProductConfigs(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/products/group_product_config/${encodeURIComponent(args.id)}`,
    args
  )
}

export function addGroupProductConfigs(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/products/group_product_config/${encodeURIComponent(
      args.product_guid
    )}`,
    args
  )
}

export function getGroupProductConfigs(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`api/products/group_product_config/${args}`)
}

export function getExtendedFamilyProductConfigs(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/extended_membership_config/${args}/all?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function updateExtendedFamilyProductConfigs(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/extended_membership_config/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function addExtendedFamilyProductConfigs(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/membership_configs?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function createPartnerConfigGroup(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/products/group_product_config/${encodeURIComponent(args.guid)}`,
    args
  )
}

export function getProductEnums(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/metadata/product?partner=${encodeURIComponent(getPartnerGuid())}`,
    {
      data: {}
    }
  )
}

export function createProductPremium(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post('/api/product_premiums', args)
}

export function getProductPremiums(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`/api/product_premiums/product/${args}`, {
    data: {}
  })
}

export function getProductPremiumEnums(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/metadata/product_premium?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    {
      data: {}
    }
  )
}

export function getRoles() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.get(
    `/auth/admin/realms/${EnvironmentalInformation.credentials.realm}/groups`
  )
}

export function getCurrentRoles(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.get(
    `/auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(args.id)}/groups`
  )
}

export function getCurrentUserPartners(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.get(
    `/auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(args.id)}`
  )
}

export function getLanguages() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get('/api/languages', { data: {} })
}

export function deleteUser(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return centralAuthenticationInstance.delete(
    `auth/admin/realms/${
      EnvironmentalInformation.credentials.realm
    }/users/${encodeURIComponent(args.id)}`
  )
}

export function deletePremium(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(`/api/premiums/${args}`, { data: {} })
}

export function addMissingPayment(args) {
  // use correct API host when API part is done /api/wallet_money_transactions
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/payments?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function updatePayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/payments/${encodeURIComponent(
      args.paymentGuid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

// Re-submits a Failed payment so it is allocated to its policy (e.g. once a
// lapsed policy has been reinstated). `reason` is required by the API and is
// kept on the payment for audit.
export function replayPayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/payments/${encodeURIComponent(
      args.paymentGuid
    )}/replay?partner=${encodeURIComponent(getPartnerGuid())}`,
    { reason: args.reason }
  )
}

export function addSingleBundledPayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put('/api/premiums/add_with_prerequisite', args)
}

export function addSingleNonBundledPayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put('/api/premiums/add_single_non_bundled', args)
}

export function addMultipleBundledPayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/premiums/add_bulk_bundle_payment?partner=${encodeURIComponent(
      args.partner
    )}`,
    args.file,
    {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
  )
}

export function addMultipleNonBundledPayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/premiums/add_bulk_non_bundled_payments?partner=${encodeURIComponent(
      args.partner
    )}`,
    args.file,
    {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
  )
}

export function sendRecurringPaymentRecommendationSms(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/customers/${encodeURIComponent(
      payload.customerGuid
    )}/send_recurring_sms`
  )
}

export function changeUserPassword(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/users/changepassword/${encodeURIComponent(args.guid)}`,
    args
  )
}

export function changeLoyaltyRegistration(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put('/api/customers/loyalty_registration', args)
}

export function pullCustomerKyc(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const customerGuid = args.guid
  return crudApiInstance.put(
    `/api/customers/${encodeURIComponent(
      customerGuid
    )}/request_kyc?send_sms=false`,
    { data: {} }
  )
}

export function getSignedUrlForAnyBucket(file, callback, bucketName) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `/api/knowledge-base/signed-url?partner=${encodeURIComponent(
        getPartnerGuid()
      )}&filename=${file.name}`
    )
    .then(response => {
      callback(response.data)
    })
    .catch(err => {
      console.log('ERROR GETTING SIGNED URL FOR BUCKET: ', err)
    })
}

export function getS3BucketSignedUrl(filename, callback) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `/api/loyaltyrenewal/upload/signed_url?partner=${encodeURIComponent(
        getPartnerGuid()
      )}&filename=${filename.name}`
    )
    .then(response => {
      callback({
        signedUrl: response.data.signed_url,
        bucketKey: response.data.filename
      })
    })
}

export function getS3BucketSignedUrlForGroupUploadResources(
  filename,
  callback
) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .post(
      `/api/groups/uploads?partner=${encodeURIComponent(
        getPartnerGuid()
      )}&filename=${filename.name}`
    )
    .then(response => {
      callback(response.data)
    })
}

export function getS3BucketSignedUrlForClaims(file, callback, claim_guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `/api/claims/upload/${encodeURIComponent(claim_guid)}?filename=${
        file.name
      }`
    )
    .then(response => {
      callback(response.data)
    })
}

export function getS3BucketSignedUrlForPolicyDcpDocuments(
  policy_guid,
  file,
  callback
) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `api/policies/complex/documents/signed_url?policy_guid=${encodeURIComponent(
        policy_guid
      )}&filename=${file.name}&partner=${encodeURIComponent(getPartnerGuid())}`
    )
    .then(response => {
      callback(response.data)
    })
}

export function getS3BucketSignedUrlForProofOfPaymentsDocuments(
  paymentPayload,
  file,
  callback,
  errorCallback
) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  crudApiInstance
    .post(
      `/api/payments?partner=${encodeURIComponent(getPartnerGuid())}`,
      paymentPayload
    )
    .then(response => {
      localStorage.setItem('payment_guid', response.data.guid)
      crudApiInstance
        .get(
          `api/payments/documents/signed_url?policy_guid=${encodeURIComponent(
            paymentPayload.policy_guid
          )}&payment_guid=${encodeURIComponent(response.data.guid)}&filename=${
            file.name
          }&partner=${encodeURIComponent(getPartnerGuid())}`
        )
        .then(response => {
          callback(response.data)
        })
        .catch(error => {
          if (errorCallback) errorCallback(error)
        })
    })
    .catch(error => {
      if (errorCallback) errorCallback(error)
    })
}

export function uploadPolicyDocument(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  // Increase timeout for document attachment API call
  // Document attachment involves policy lookup, validation, S3 URL registration, and audit events
  // Default 30s timeout is too short for slow database connections
  const config = {
    timeout: 90000 // 90 seconds (up from default 30s)
  }

  return crudApiInstance.post(
    `api/policies/complex/documents?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args,
    config
  )
}

// Cashback awards from the cashback service (what a customer earns for a full year of cover without a claim).
export function getCashbackAwards(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const status = params && params.status ? `&status=${params.status}` : ''
  return crudApiInstance.get(
    `/api/cashback_rewards?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&page=0&page_size=2000${status}`
  )
}

export function getCashbacks(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/cashback_rewards?customer_guid=${encodeURIComponent(
      args.customer_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}&page=0&page_size=100`
  )
}

export function getS3BucketSignedUrlForCustomerFile(
  filename,
  callback,
  customerGuid
) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  customerApplicationInstance
    .get(
      `api/customers/files/upload/${encodeURIComponent(
        customerGuid
      )}?filename=${filename.name}&partner=${encodeURIComponent(
        getPartnerGuid()
      )}`
    )
    .then(response3 => {
      const url = response3.data
      callback({ signedUrl: url.signedUrl })
    })
    .then(getAllCustomerFiles(customerGuid))
}

export function getS3BucketSignedUrlForBulksms(file, callback) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  bulkSmsInstance
    .get(`/api/sms_batch/upload?filename=${file.name}`)
    .then(response => {
      callback(response.data)
    })
}

export function getAllCustomerFiles(customerGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/files/${encodeURIComponent(
      customerGuid
    )}/all?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getAllPolicies(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/customer/guid/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getAllPayments(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/payments/all/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getAllPremiums(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/premiums/customer/${args.msisdn}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function getAllMandates(msisdn) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/debit_orders/customer/${msisdn}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

// TODO: Pull the country code from the user information
export function getClaimsHospital() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`api/claim_hospitals?country=${countryCode()}`, {
    data: {}
  })
}

export function getClaimRejectionReason(arg) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`/api/configs/claim_rejection_reasons`, {
    data: {}
  })
}

export function getClosedCallback(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const page = args.page ? args.page : 1
  const page_size = args.page_size ? args.page_size : 10
  return crudApiInstance.get(
    `/api/claim_callbacks?open=false&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&page=${page}&size=${page_size}`
  )
}

export function getOpenCallback(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const page = args.page ? args.page : 1
  const page_size = args.page_size ? args.page_size : 10
  return crudApiInstance.get(
    `/api/claim_callbacks?open=true&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&page=${page}&size=${page_size}`
  )
}

export function getAllCashBacks() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/cashback?partnerGuid=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function markCashbackAsPaid(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/cashback/mark_as_paid?id=${args}&partnerGuid=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function markCashbackAsUnpaid(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/cashback/mark_as_unpaid?id=${args}&partnerGuid=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function getReportBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/premium_received_report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}`
  )
}

export function getReportPaymentBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/payment_received_report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}`
  )
}

export function getDailyReportBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `/api/reports/daily_stats_report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}`
  )
}

export function getPoliciesReportBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/all_created_policies?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}`
  )
}

export function getClaimFinalisationReportBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/claim_finalisation_report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}`
  )
}

export function getReportRegistrationBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/registered_customers?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.startDate
    }&endDate=${args.endDate}`
  )
}

export function getReportPayableClaimsBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/claims_payable_report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getReportSuspendedPoliciesBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/policy_suspended?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getReportPreAuthorizationBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/preauthorization?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}`
  )
}

export function getReportClaimDeathBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/death-claim-report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}`
  )
}

export function getReportClaimFinalizationDeathBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/death-claim-report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}&status='Approved'`
  )
}

export function getReportClaimHospitalBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/hospital-claim-report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}`
  )
}

export function getReportClaimFinalizationHospitalBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/hospital-claim-report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.start_date
    }&endDate=${args.end_date}&status='Approved'`
  )
}
export function getReportClaimReimbursementBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/reimbursement?type=${args.type}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&startDate=${args.start_date}&endDate=${args.end_date}`
  )
}

export function getReportClaimFinalizationReimbursementBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/reimbursement?type=${args.type}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&startDate=${args.start_date}&endDate=${args.end_date}&status='Approved'}`
  )
}

export function getReportOpenClaimBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/all_open_claims_report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getReportRefundBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/refunds_report?type=${args.type}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&startDate=${args.startDate}&endDate=${args.endDate}`
  )
}

export function uploadTigoLoyaltyEligibility(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/loyalty_eligibility/local_upload_csv?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function renewLoyalty() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/loyalty_eligibility/renew_loyalty?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    { data: {} }
  )
}

export function createDebitOrderz(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return paymentServiceInstance.post(
    `/api/debit_orders?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function deleteDebitOrderz(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return paymentServiceInstance.delete(
    `/api/debit_orders/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function editDebitOrderz(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return paymentServiceInstance.post(
    `/api/debit_orders?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function getDebitOrderz(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  if (args.policy_guid) {
    return paymentServiceInstance.get(
      `api/debit_orders/policy?partner=${encodeURIComponent(
        getPartnerGuid()
      )}&policy_guid=${encodeURIComponent(args.policy_guid)}`
    )
  }
  return paymentServiceInstance.get(
    `api/debit_orders?partner=${encodeURIComponent(getPartnerGuid())}&msisdn=${
      args.msisdn
    }`
  )
}

export function createDebitOrder(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/debit_orders?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function getAllBanks() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`/api/banks/country/${countryCode()}`)
}

export function getDebitOrderTransactions() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/debit_order_transactions?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    { data: {} }
  )
}

export function updateDebitOrderTransactions(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/debit_order_transactions/process?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&status=${args.status}`,
    args.transactions
  )
}

export function getPendingDebitOrderTransactions() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/debit_order_transactions/pending?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    { data: {} }
  )
}

export function getFailedDebitOrderTransactions() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/debit_order_transactions/failed?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    { data: {} }
  )
}

export function preRegisterGroup(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/groups/pre_register?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function registerGroup(groupId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // return crudeInstance.post(`/api/groups/register?partner=${encodeURIComponent(getPartnerGuid())}`, args);
  return crudApiInstance.post(
    `/api/groups/v2/register?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&group_id=${encodeURIComponent(groupId)}`
  )
}

export function getActualGroupQuote(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/v2/quotation?group_id=${args}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function updateGroup(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/groups/pre_register?groupId=${encodeURIComponent(
      args.groupId
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function getGroups(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/user?partner=${encodeURIComponent(getPartnerGuid())}&page=${
      args.page
    }&size=${args.size}`
  )
}
export function getGroup(groupId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/group/${encodeURIComponent(
      groupId
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}
export function deleteGroup(groupId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/groups/${encodeURIComponent(groupId)}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}
export function getAllGroups(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups?partner=${encodeURIComponent(getPartnerGuid())}&page=${
      args.page
    }&size=${args.size}`
  )
}

export function addGroupPayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(`/api/group-payments/create`, args)
}

export function getCurrentGroupMembers(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/${encodeURIComponent(
      args.groupId
    )}/customers?partner=${encodeURIComponent(getPartnerGuid())}&page=${
      args.page
    }&size=${args.size}`
  )
}

export function getCurrentGroupMembersForAdmins(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/${encodeURIComponent(
      args.groupId
    )}/members?partner=${encodeURIComponent(getPartnerGuid())}&page=${
      args.page
    }&size=${args.size}`
  )
}

export function getGroupMembersForCSV(groupId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/${encodeURIComponent(
      groupId
    )}/members?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// `limit` is the page size this endpoint actually reads; `size` is ignored.
// Confirmed against staging on a 20-policy group: size=100 returned the
// service default of 10 and echoed limit:10, while limit=100 returned all 20
// and echoed limit:100. `page` is 1-based and works once limit is supplied.
// `size` is still sent for callers/back ends that expect the older name.
export function getCurrentGroupPolicies(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const limit = args.limit || args.size
  return crudApiInstance.get(
    `/api/groups/${encodeURIComponent(
      args.groupId
    )}/policies?partner=${encodeURIComponent(getPartnerGuid())}&page=${
      args.page
    }&size=${limit}&limit=${limit}`
  )
}

export function getCurrentGroupPremiums(groupId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/${encodeURIComponent(groupId)}/premiums`
  )
}

export function getCurrentGroupPayments(groupId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/group-payments/${encodeURIComponent(groupId)}`
  )
}

export function getCurrentGroupClaims(groupId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/${encodeURIComponent(groupId)}/claims`
  )
}

export function getCurrentGroupClaimCallbacks(groupId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/callbacks?group_id=${encodeURIComponent(groupId)}`
  )
}

export function createGroupClaimCallBack(msisdn) {
  return crudApiInstance.post(
    `public/groups/call_back?partner=britamguid&msisdn=${msisdn}`
  )
}

export function getGroupProducts(args) {
  return crudApiInstance.get(`/public/groups/products?partner=${args}`)
}

export function getPolicyProduct(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/${args}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getAllProducts() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getAllCustomerClaims(msisdn) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/claims/customer/${msisdn}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function getTotalPayablePremium(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/${args}/premium_rate?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function initiatePolicyMobileMoneyPayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  // eslint-disable-next-line camelcase
  const partnerGuid = getPartnerGuid()
  return paymentServiceInstance.post(
    `/api/payment_requests/direct?partner=${encodeURIComponent(partnerGuid)}`,
    args
  )
}

export function generatePolicyCheckoutLink(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  // eslint-disable-next-line camelcase
  const partnerGuid = getPartnerGuid()
  return paymentServiceInstance.post(
    `/api/payment_requests/checkout?partner=${encodeURIComponent(partnerGuid)}`,
    args
  )
}

export function getRealPayContract(policyGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  // eslint-disable-next-line camelcase
  const partnerGuid = getPartnerGuid()
  return paymentServiceInstance.get(
    `/api/payment_requests/realpay/policies/${encodeURIComponent(
      policyGuid
    )}?partner=${encodeURIComponent(partnerGuid)}`
  )
}

export function extendRealPaySchedule(policyGuid, targetMonths) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  // eslint-disable-next-line camelcase
  const partnerGuid = getPartnerGuid()
  return paymentServiceInstance.post(
    `/api/payment_requests/realpay/policies/${encodeURIComponent(
      policyGuid
    )}/extend?partner=${encodeURIComponent(partnerGuid)}`,
    targetMonths ? { target_months: targetMonths } : {}
  )
}

export function retryRealPayInstalment(policyGuid, instalmentReference, date) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  // eslint-disable-next-line camelcase
  const partnerGuid = getPartnerGuid()
  return paymentServiceInstance.post(
    `/api/payment_requests/realpay/policies/${encodeURIComponent(
      policyGuid
    )}/instalments/${encodeURIComponent(
      instalmentReference
    )}/retry?partner=${encodeURIComponent(partnerGuid)}`,
    date ? { date } : {}
  )
}

export function claimUploadCallback(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  if (args.customerFileUpload) {
    return customerApplicationInstance.post(
      `api/customers/files?partner=${encodeURIComponent(getPartnerGuid())}`,
      {
        type: args.type,
        url: args.signedUrl,
        customer_guid: args.customerGuid,
        filename: 'foobar'
      }
    )
  }
  return crudApiInstance.put('/api/claims/upload_callback', args)
}

export function createClaimFile(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/claims/${encodeURIComponent(args.claim_guid)}/files`,
    args
  )
}

export function claimFileDelete(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/claims/files/${encodeURIComponent(args.guid)}`
  )
}

export function claimCallbackDelete(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/claim_callbacks/close/${encodeURIComponent(args.claimGuid)}`,
    args.reason
  )
}

export function getVasAgentReportBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/vas_agents?type=${args.type}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&startDate=${args.startDate}&endDate=${args.endDate}`
  )
}

export function getReportAgentWebBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/web_agents?type=${args.type}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&startDate=${args.startDate}&endDate=${args.endDate}`
  )
}

export function getClaimInfo(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`/api/claims/${args}`)
}

export function retryFailedClaimPayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/mobile_money_transactions/retry_failed_claim_payout/${args}`
  )
}

export function updateClaimPaymentOutcome(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put('/api/mobile_money_transactions/outcome', args)
}

export function getClaimConditionList() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get('/api/configs/claim_conditions', { data: {} })
}

export function uploadBulkSms(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return bulkSmsInstance.post('/api/sms_batch', args)
}

export function downloadBulkSms(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return bulkSmsInstance.get(
    `/api/sms_batch/download/${args}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function getBulkSmsSummary(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return bulkSmsInstance.get(
    `/api/sms_batch/${args}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function mapColumnsToFields(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  return loyaltyInstance.post(
    `/upload?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function getPreviousLoyaltyStatus(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  return loyaltyInstance.get(
    `/report?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&report_id=${encodeURIComponent(args.guid)}`
  )
}

export function getPreviousLoyaltiesStatus() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  axios.defaults.headers.get['engine-user-token'] = getAuthorizationToken()

  return loyaltyInstance.get(
    `/reports?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function stopBlast(blastId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return bulkSmsInstance.put(
    `/api/sms_batch/interrupt/${encodeURIComponent(
      blastId
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getBatchProgress(blastId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return bulkSmsInstance.get(
    `/api/sms_batch/progress/${encodeURIComponent(
      blastId
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getReportPaidClaimBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/claims_paid_report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.startDate
    }&endDate=${args.endDate}`
  )
}

export function getReportClaimNotificationBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/claim_notification_report?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.startDate
    }&endDate=${args.endDate}`
  )
}

export function getReportClaimRegisterBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/claims_register?type=${args.type}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&startDate=${args.startDate}&endDate=${args.endDate}`
  )
}

export function getReportPremiumRegisterBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/premium_register?type=${
      args.type
    }&partner=${encodeURIComponent(getPartnerGuid())}&startDate=${
      args.startDate
    }&endDate=${args.endDate}`
  )
}

export function getReportPremiumDueBlob(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/premium_due?type=${args.type}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function addRemittances(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/remittance/add_bulk_remittances?partner=${encodeURIComponent(
      args.partner
    )}`,
    args.file,
    {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
  )
}

//
export function getTempMembers(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/groups/temp/all?page=${args.page}&size=${
      args.size
    }&groupId=${encodeURIComponent(args.groupId)}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

// add
export function addPrincipal(principal) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/groups/temp/add/principal?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    principal
  )
}

export function addDependant(dependant) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/groups/temp/add/dependant?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    dependant
  )
}

// edit
export function editDependant(dependant) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/groups/temp//edit/dependant/id/${encodeURIComponent(
      dependant.id
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    dependant
  )
}

export function editPrincipal(principal) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/groups/temp/edit/principal/id/${encodeURIComponent(
      principal.id
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    principal
  )
}

// delete
export function deletePrincipal(principalId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/groups/temp/delete/principal/id/${encodeURIComponent(
      principalId
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function deleteDependant(dependantId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/groups/temp/delete/dependant/id/${encodeURIComponent(
      dependantId
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function uploadExcel(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/groups/temp/upload?partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function getCustomerDependant(dependantId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/dependants/${encodeURIComponent(
      dependantId
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getCustomerDependants(customerId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/dependants/${encodeURIComponent(
      customerId
    )}/all?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function addCustomerDependant(dependant) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.post(
    `api/dependants/${encodeURIComponent(
      dependant.customer_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    dependant
  )
}

export function updateCustomerDependant(dependant) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.put(
    `api/dependants/${encodeURIComponent(
      dependant.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    dependant
  )
}

export function deleteCustomerDependant(dependantGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.delete(
    `api/dependants/${encodeURIComponent(
      dependantGuid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getCustomerBeneficiary(customer_guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/${encodeURIComponent(
      customer_guid
    )}/beneficiary?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function addCustomerBeneficiary(beneficiary) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.post(
    `api/beneficiaries?partner=${encodeURIComponent(getPartnerGuid())}`,
    beneficiary
  )
}
export function deleteCustomerbeneficiary(beneficiary) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.delete(
    `api/beneficiaries/${encodeURIComponent(
      beneficiary.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateCustomerBeneficiary(beneficiary) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.put(
    `api/beneficiaries/${encodeURIComponent(
      beneficiary.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    beneficiary
  )
}

export function getCustomerBeneficiaryBankDetails(customer_guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/bank_details/${encodeURIComponent(
      customer_guid
    )}/beneficiary_customer?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function addCustomerBeneficiaryBankDetails(bankdetails) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.post(
    `api/bank_details?partner=${encodeURIComponent(getPartnerGuid())}`,
    { ...bankdetails, customer_guid: null }
  )
}
export function deleteCustomerbeneficiaryBankDetails(bankdetails) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.delete(
    `api/bank_details/${encodeURIComponent(
      bankdetails.customer_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateCustomerBeneficiaryBankDetails(bankdetails) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.put(
    `api/bank_details/${encodeURIComponent(
      bankdetails.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    bankdetails
  )
}

export function getCustomerBankDetails(customer_guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/${encodeURIComponent(
      customer_guid
    )}/bank_details?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function addCustomerBankDetails(bankdetails) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.post(
    `api/bank_details?partner=${encodeURIComponent(getPartnerGuid())}`,
    bankdetails
  )
}
export function deleteCustomerBankDetails(bankdetails) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.delete(
    `api/bank_details/${encodeURIComponent(
      bankdetails.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateCustomerBankDetails(bankDetails) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.put(
    `api/bank_details/${encodeURIComponent(
      bankDetails.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    bankDetails
  )
}

export function getCustomerDocument(dependantId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/files/${encodeURIComponent(
      dependantId
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getCustomerDocuments(customerId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.get(
    `api/customers/files/${encodeURIComponent(
      customerId
    )}/all?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function addCustomerDocument(document) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.post(
    `api/customers/files?partner=${encodeURIComponent(getPartnerGuid())}`,
    document
  )
}

export function updateCustomerDocument(document) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.put(
    `api/customers/files/${encodeURIComponent(
      document.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    document
  )
}

export function deleteCustomerDocument(documentGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return customerApplicationInstance.delete(
    `api/customers/files/${encodeURIComponent(
      documentGuid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function deletePolicyDocument(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/policies/complex/documents?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&policy_doc_guid=${encodeURIComponent(
      args.policy_doc_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getS3BucketSignedUrlForCustomerDocuments(
  customerGuid,
  file,
  callback
) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  customerApplicationInstance
    .get(
      `api/customers/files/upload/${encodeURIComponent(
        customerGuid
      )}?partner=${encodeURIComponent(getPartnerGuid())}&filename=${file.name}`
    )
    .then(response => {
      callback(response.data)
    })
}

export function getS3BucketSignedUrlForClaimDcpDocuments(
  claim_guid,
  file,
  callback
) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `api/claims/complex/documents/signed_url?claim_guid=${encodeURIComponent(
        claim_guid
      )}&filename=${file.name}&partner=${encodeURIComponent(getPartnerGuid())}`
    )
    .then(response => {
      callback(response.data)
    })
    .catch(err => {
      console.log('ERROR GETTING S3 BUCKET SIGNED URL: ', err)
    })
}

export function getS3BucketSignedUrlForBulkPolicyOnboarding(file, callback) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `api/bulk-policy-onboarding/signed_url?filename=${
        file.name
      }&partner=${encodeURIComponent(getPartnerGuid())}`
    )
    .then(response => {
      callback(response.data)
    })
}

export function getS3BucketSignedUrlForEmailTemplates(file, callback) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `api/email/templates/upload/signed_url?partner=${encodeURIComponent(
        getPartnerGuid()
      )}&filename=${file.name}`
    )
    .then(response => {
      callback({
        signedUrl: response.data.signed_url,
        bucketKey: response.data.filename
      })
    })
}

export function getS3BucketSignedUrlForBulkPayments(file, callback) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `/api/bulk_payments/upload/signed_url?filename=${
        file.name
      }&partner=${encodeURIComponent(getPartnerGuid())}`
    )
    .then(response => {
      callback({
        signedUrl: response.data.signed_url,
        bucketKey: response.data.filename
      })
    })
}

export function saveClaimDcpDocument(doc) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/claims/complex/documents?claim_guid=${encodeURIComponent(
      doc.claim_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    doc
  )
}

export function saveBulkPolicyOnboarding(doc) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/bulk-policy-onboarding?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    doc
  )
}

export function getBulkPoliciesOnboarding(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const page = args.page ? args.page : 1
  const page_size = args.page_size ? args.page_size : 10
  return crudApiInstance.get(
    `api/bulk-policy-onboarding/batches?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&page=${page}&page_size=${page_size}`
  )
}

export function getFailedBulkPoliciesBatches(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const page = args.page ? args.page : 1
  const page_size = args.page_size ? args.page_size : 10
  return crudApiInstance.get(
    `api/bulk-policy-onboarding/failed_batch_entries?batch_guid=${encodeURIComponent(
      args.batch_guid
    )}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}&page=${page}&page_size=${page_size}`
  )
}

// bulk payments
export function getBulkPaymentsBatches(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const page = payload.page ? payload.page : 1
  const size = payload.page_size ? payload.page_size : 10
  return crudApiInstance.get(
    `api/bulk_payments?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&page=${page}&size=${size}`
  )
}

export function addBulkPayments(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/bulk_payments?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function saveBulkPayments(doc) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/bulk_payments?partner=${encodeURIComponent(getPartnerGuid())}`,
    doc
  )
}

export function getFailedBulkPaymentsBatches(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const page = args.page ? args.page : 1
  const size = args.page_size ? args.page_size : 10
  return crudApiInstance.get(
    `api/bulk_payments/${encodeURIComponent(
      args.batch_guid
    )}/transactions?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&size=${size}&page=${page}`
  )
}

export function getTemplateDocuments(type) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  let query = `partner=${encodeURIComponent(getPartnerGuid())}`
  if (type) {
    query += `&type=${type}`
  }

  return crudApiInstance.get(`api/templates?${query}`)
}

export function getEmailTemplates(type) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  let query = `partner=${encodeURIComponent(getPartnerGuid())}`
  if (type) {
    query += `&type=${type}`
  }

  return crudApiInstance.get(`api/email/templates?${query}`)
}

// Email template CRUD (new model: api/email_templates)
export function getEmailTemplatesV2(type) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`

  let query = `partner=${encodeURIComponent(getPartnerGuid())}`
  if (type) {
    query += `&type=${type}`
  }

  return crudApiInstance.get(`api/email_templates?${query}`)
}

export function addGuide(guide) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/knowledge-base?partner=${encodeURIComponent(getPartnerGuid())}`,
    guide
  )
}

export function editGuide(guide) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/knowledge-base/${encodeURIComponent(
      guide.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    guide
  )
}

export function deleteGuide(guid) {
  console.log('deleteGuide: ', guid)
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/knowledge-base/${encodeURIComponent(
      guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getGuide(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/knowledge-base/${encodeURIComponent(
      guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getGuides() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/knowledge-base?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function addTemplateDocument(template) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/templates?partner=${encodeURIComponent(getPartnerGuid())}`,
    template
  )
}

export function addEmailTemplate(template) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/email/templates?partner=${encodeURIComponent(getPartnerGuid())}`,
    template
  )
}

export function addEmailTemplateV2(template) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/email_templates?partner=${encodeURIComponent(getPartnerGuid())}`,
    template
  )
}

export function deleteEmailTemplate(templateGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/email/templates/${encodeURIComponent(
      templateGuid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function updateEmailTemplateV2(template) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/email_templates/${encodeURIComponent(
      template.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    template
  )
}

export function deleteEmailTemplateV2(templateGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/email_templates/${encodeURIComponent(
      templateGuid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function addReportTemplate(template) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/reports?partner=${encodeURIComponent(getPartnerGuid())}`,
    template
  )
}

export function getReportsTemplates() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/reports?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function generateReport(args) {
  const reportId = args.id

  // Remove 'id' from query parameters
  delete args.id

  const queryLsit = []
  Object.keys(args).forEach(k => queryLsit.push(`${k}=${args[k]}`))
  queryLsit.join('&')

  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return reportsInstance.get(
    `api/reports/${encodeURIComponent(
      reportId
    )}/generate?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&${queryLsit.join('&')}`
  )
}

export function editTemplateDocument(template) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/templates/${encodeURIComponent(
      template.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    template
  )
}

export function deleteTemplateDocument(documentGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/templates/${encodeURIComponent(
      documentGuid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function editReportTemplate(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/reports/${encodeURIComponent(args.id)}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    args
  )
}

export function deleteReportTemplate(id) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/reports/${encodeURIComponent(id)}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function getS3BucketSignedUrlForPartnerTemplateDocuments(
  file,
  callback
) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `api/templates/upload/signed_url?partner=${encodeURIComponent(
        getPartnerGuid()
      )}&filename=${file.name}`
    )
    .then(response => {
      console.log(response.data.signed_url)
      callback({
        signedUrl: response.data.signed_url,
        bucketKey: response.data.filename
      })
    })
}

export function getS3BucketSignedUrlForEmailTemplatesV2(file, callback) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  crudApiInstance
    .get(
      `api/email_templates/upload/signed_url?partner=${encodeURIComponent(
        getPartnerGuid()
      )}&filename=${file.name}`
    )
    .then(response => {
      callback({
        signedUrl: response.data.signed_url,
        bucketKey: response.data.filename
      })
    })
}

export function getCustomerRegistrationStatusByGuid(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/customers/registration_status_by_guid?customer_guid=${encodeURIComponent(
      guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// Check whether a customer is already registered for this partner by MSISDN.
// Returns RegistrationStatusDTO { registered, guid, first_name, surname, ... }.
export function checkCustomerRegistration(msisdn) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/customers/registration_status?msisdn=${encodeURIComponent(
      msisdn
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getElasticSearchResults(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/search?partner=${encodeURIComponent(getPartnerGuid())}&search=${
      args.query
    }&doc_type=${args.document_type}&page=${args.page}&size=${args.size}`
  )
}

export function getEmailTemplateByGuid(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/email/templates/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function sendEmail(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/emails/send?partner_guid=${encodeURIComponent(
      getPartnerGuid()
    )}&email_template_guid=${encodeURIComponent(
      args.email_template_guid
    )}&email_recipients=${
      args.email_recipients
    }&entity_guid=${encodeURIComponent(args.entity_guid)}&email_code=${
      args.email_code
    }`,
    args
  )
}

export function buyUnderWriterPolicy(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/onboard_policy?partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function assignLawyerClaim(args) {
  console.log('assignLawyerClaim: ', args)
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/claims/complex?claim_guid=${encodeURIComponent(
      args.additional_details.claim_guid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    args
  )
}

export function getPolicyByGuidDcp(args) {
  console.log('getPolicyByGuidDcp: ', args)
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/policies/complex?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&policy_number=${args.policy_number}&partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

// Samba Policy Cancellation Requests
export function getPolicyCancellationRequests(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const params = new URLSearchParams()
  params.append('partner', 'samba')
  if (args.customer_guid) params.append('customer_guid', args.customer_guid)
  if (args.product_guid) params.append('product_guid', args.product_guid)
  if (args.policy_guid) params.append('policy_guid', args.policy_guid)
  if (args.from) params.append('from', args.from)
  if (args.to) params.append('to', args.to)
  if (args.status) params.append('status', args.status)
  params.append('page', args.page || 1)
  params.append('size', args.size || 10)
  return crudApiInstance.get(
    `/api/policies/complex/cancel_callback?${params.toString()}`
  )
}

export function updatePolicyCancellationRequest(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/policies/complex/cancel_callback/${encodeURIComponent(
      args.policy_guid
    )}?partner=samba`,
    {
      status: args.status,
      reason: args.reason
    }
  )
}

export function updatePolicyCancellationInProgress(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/policies/complex?policy_guid=${encodeURIComponent(
      args.policy_guid
    )}&partner=samba`,
    {
      additional_details: args.additional_details
    }
  )
}

export function createCancellationRequest(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post('api/policies/complex/cancellations', payload)
}

// Authorization is set per-request (not on the global axios.defaults) so the
// bearer token never leaks onto unrelated axios calls — notably a direct S3
// PUT below, whose whole premise is to carry NO Authorization header.
function authConfig() {
  return { headers: { Authorization: `Bearer ${getAuthorizationToken()}` } }
}

// AI Drafting: upload a document → enqueue an async drafting job, then poll it.
export function pollDraftJob(jobId) {
  return crudApiInstance.get(
    `/api/products/drafts/${encodeURIComponent(
      jobId
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    authConfig()
  )
}

// AI-draft upload follows the webclient's signed-URL pattern (presigned S3),
// matching the backend's secure signed-URL/import contract:
//   1. getDraftSignedUrl(filename)  → { signedUrl, bucketKey }
//   2. putDraftDocumentToS3(...)    → PUT the file straight to S3 (NO auth header)
//   3. importDraftDocument(bucketKey) → { job_id }   (then poll via pollDraftJob)

// The backend pins ContentType on the presigned PUT (derived from the extension
// of the filename we send to step 1), so the PUT must send the SAME type.
function draftDocumentContentType(filename) {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'pdf') return 'application/pdf'
  if (ext === 'docx') {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  }
  return 'application/octet-stream'
}

export function getDraftSignedUrl(filename) {
  return crudApiInstance.post(
    `/api/products/drafts/signed-url?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&filename=${encodeURIComponent(filename)}`,
    {},
    authConfig()
  )
}

export function putDraftDocumentToS3(signedUrl, file) {
  // A presigned S3 URL is self-authenticating — it must NOT carry our bearer
  // token. Use a clean axios instance and strip any inherited Authorization
  // default so the token never leaks to S3.
  const s3 = axios.create()
  delete s3.defaults.headers.common.Authorization
  return s3.put(signedUrl, file, {
    headers: { 'Content-Type': draftDocumentContentType(file.name) },
    transformRequest: [data => data] // send the File/Blob as-is, don't JSON-encode
  })
}

export function importDraftDocument(bucketKey) {
  return crudApiInstance.post(
    `/api/products/drafts/import?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&bucketKey=${encodeURIComponent(bucketKey)}`,
    {},
    authConfig()
  )
}

// List every draft ever generated for the current partner.
// Returns summary rows: [{ guid, job_id, status, product_name, imported,
// imported_product_guid, created_at, ... }] — newest-first from the backend.
export function getPartnerDrafts() {
  return crudApiInstance.get(
    `/api/products/drafts?partner=${encodeURIComponent(getPartnerGuid())}`,
    authConfig()
  )
}

// Full draft detail (incl. the AI result / draft_product) for the review page.
export function getDraftDetail(jobId) {
  return crudApiInstance.get(
    `/api/products/drafts/${encodeURIComponent(
      jobId
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    authConfig()
  )
}

// Approve a reviewed draft → deep-import the full product graph.
export function importDraftProduct(jobId) {
  return crudApiInstance.post(
    `/api/products/drafts/${encodeURIComponent(
      jobId
    )}/import?partner=${encodeURIComponent(getPartnerGuid())}`,
    {},
    authConfig()
  )
}

// Product-template catalog: the static archetype blueprints (funeral, group
// scheme, credit-life, medical cash). List the summaries, then clone one into a
// live partner Product (the backend materializes its rating nodes/tables).
export function getProductTemplateCatalog() {
  return crudApiInstance.get('/api/product-template-catalog', authConfig())
}

export function cloneProductTemplate(key) {
  return crudApiInstance.post(
    `/api/product-template-catalog/${encodeURIComponent(
      key
    )}/clone?partner=${encodeURIComponent(getPartnerGuid())}`,
    {},
    authConfig()
  )
}

export function getProductRatingNodes(product_guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/rating-nodes/product/${encodeURIComponent(
      product_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getProductSnapshots(productGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/${encodeURIComponent(
      productGuid
    )}/snapshots?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getProductSnapshot(productGuid, version) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/products/${encodeURIComponent(
      productGuid
    )}/snapshots/${version}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createProductRatingNode(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // partner is routing-only (query string); CreateRatingNodeDto only declares
  // partner_guid, so the body copy is rejected as an unknown field
  // (forbidNonWhitelisted → 400 "property partner should not exist").
  const { partner, ...body } = args
  return crudApiInstance.post(
    `/api/rating-nodes?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function updateProductRatingNode(node_guid, args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  // Defensive strip, same as create — callers shouldn't include it, but the
  // DTO would reject it outright if one ever did.
  const { partner, ...body } = args
  return crudApiInstance.put(
    `/api/rating-nodes/${encodeURIComponent(
      node_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

export function deleteProductRatingNode(node_guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/rating-nodes/${encodeURIComponent(
      node_guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// ── Rating Tables (partner-scoped, reusable rate matrices) ──────────────────
export function getRatingTables() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/rating-tables?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createRatingTable(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/rating-tables?partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function updateRatingTable(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const v = args.version != null ? `&version=${args.version}` : ''
  return crudApiInstance.put(
    `/api/rating-tables/${encodeURIComponent(
      args.guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}${v}`,
    args.payload
  )
}

export function activateRatingTable(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.patch(
    `/api/rating-tables/${encodeURIComponent(
      guid
    )}/activate?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function deactivateRatingTable(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.patch(
    `/api/rating-tables/${encodeURIComponent(
      guid
    )}/deactivate?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function deleteRatingTable(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/rating-tables/${encodeURIComponent(
      guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// ── Rating bulk import (workbook → rating tables + nodes) ────────────────────
// The pricing team downloads a generated workbook, fills in flat rates and
// uploads it once for the whole product. Multipart throughout, not the
// presigned-S3 pattern used for AI drafting: /validate has no bucketKey
// variant on the backend (multipart-only), and a rate-card workbook is tiny
// next to a BRD — the backend caps it at RATING_IMPORT_MAX_FILE_BYTES (5MB
// default, vs 50MB for drafting), so there's no large-upload problem to solve.
// Both endpoints take the SAME File object; the browser keeps it in memory
// between the dry-run call and the real submit, so nothing is re-uploaded to
// intermediate storage. FormData is handed to axios untouched: its xhr
// adapter deletes the instance's default JSON Content-Type when the body is
// FormData, so the browser sets the multipart boundary itself (verified in
// axios 0.15.3).

// The generated workbook for a product, as a blob for a download anchor.
export function getRatingImportTemplate(productGuid) {
  const config = authConfig()
  config.responseType = 'blob'
  return crudApiInstance.get(
    `/api/rating-imports/template?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&product=${encodeURIComponent(productGuid)}`,
    config
  )
}

// Dry run — parses and validates, provably without writing. Returns the same
// report shape as the import, minus change_request_guid.
export function validateRatingWorkbook(productGuid, file) {
  const form = new FormData()
  form.append('workbook', file)
  return crudApiInstance.post(
    `/api/rating-imports/validate?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&product=${encodeURIComponent(productGuid)}`,
    form,
    authConfig()
  )
}

// Opens a RatingBulkImport change request when the workbook parses clean.
// Always answers 202 — rating changes are unconditionally four-eyes gated, so
// nothing is materialized until a different user approves the CR.
export function importRatingWorkbook(productGuid, file) {
  const form = new FormData()
  form.append('workbook', file)
  return crudApiInstance.post(
    `/api/rating-imports?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&product=${encodeURIComponent(productGuid)}`,
    form,
    authConfig()
  )
}

// ── Rating snapshots — rating history, versioned separately from the product ─
// Scoped per (product, package) — packageCode omitted/null means the
// product's add-ons, NOT "every package". Fan out per package to build a
// combined view (see RatingSnapshotsPanel).
export function getRatingSnapshots(productGuid, packageCode) {
  let url = `/api/rating-snapshots?partner=${encodeURIComponent(
    getPartnerGuid()
  )}&product=${encodeURIComponent(productGuid)}`
  if (packageCode) url += `&package=${encodeURIComponent(packageCode)}`
  return crudApiInstance.get(url, authConfig())
}

export function getRatingSnapshotDetail(
  productGuid,
  ratingVersion,
  packageCode
) {
  let url = `/api/rating-snapshots/${encodeURIComponent(
    ratingVersion
  )}?partner=${encodeURIComponent(
    getPartnerGuid()
  )}&product=${encodeURIComponent(productGuid)}`
  if (packageCode) url += `&package=${encodeURIComponent(packageCode)}`
  return crudApiInstance.get(url, authConfig())
}

// ── Change Requests (maker-checker) — partner-wide list + approve/reject ─────
export function getPartnerChangeRequests(status, domain) {
  let url = `/api/change-requests?partner=${encodeURIComponent(
    getPartnerGuid()
  )}`
  if (status) url += `&status=${status}`
  if (domain) url += `&domain=${domain}`
  return crudApiInstance.get(url, authConfig())
}

export function approveChangeRequest(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/change-requests/${encodeURIComponent(
      args.guid
    )}/approve?partner=${encodeURIComponent(getPartnerGuid())}`,
    args.reason ? { reason: args.reason } : {}
  )
}

export function rejectChangeRequest(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/change-requests/${encodeURIComponent(
      args.guid
    )}/reject?partner=${encodeURIComponent(getPartnerGuid())}`,
    args.reason ? { reason: args.reason } : {}
  )
}

// Webhook Subscriptions
export function getWebhookSubscriptions(filters = {}) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get('/api/webhooks/subscriptions', { params: filters })
}

export function createWebhookSubscription(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post('/api/webhooks/subscriptions', payload)
}

export function updateWebhookSubscription(guid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/webhooks/subscriptions/${encodeURIComponent(guid)}`,
    payload
  )
}

export function deleteWebhookSubscription(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/webhooks/subscriptions/${encodeURIComponent(guid)}`
  )
}

export function saveWebhookSubscriptionAuth(guid, authConfig) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/webhooks/subscriptions/${encodeURIComponent(guid)}/auth`,
    authConfig
  )
}

export function deleteWebhookSubscriptionAuth(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/webhooks/subscriptions/${encodeURIComponent(guid)}/auth`
  )
}

// ─── Organizations ────────────────────────────────────────────────────────────

export function getOrganizations() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`api/organizations`)
}

export function getOrganization(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(`api/organizations/${encodeURIComponent(guid)}`)
}

export function createOrganization(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(`api/organizations`, payload)
}

export function updateOrganization(guid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/organizations/${encodeURIComponent(guid)}`,
    payload
  )
}

export function deleteOrganization(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(`api/organizations/${encodeURIComponent(guid)}`)
}

// ─── Org Product Templates ────────────────────────────────────────────────────

export function getOrgProductTemplates(organizationGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/product-templates?organization_guid=${encodeURIComponent(
      organizationGuid
    )}`
  )
}

export function createOrgProductTemplate(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/product-templates?partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function updateOrgProductTemplate(guid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/product-templates/${encodeURIComponent(
      guid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function deleteOrgProductTemplate(guid, organizationGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/product-templates/${encodeURIComponent(
      guid
    )}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&organization_guid=${encodeURIComponent(organizationGuid)}`
  )
}

// ─── Org Email Templates ──────────────────────────────────────────────────────

export function getOrgEmailTemplates(organizationGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/email_templates_org?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createOrgEmailTemplate(organizationGuid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/email_templates_org?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function updateOrgEmailTemplate(guid, organizationGuid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/email_templates_org/${encodeURIComponent(
      guid
    )}?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function deleteOrgEmailTemplate(guid, organizationGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/email_templates_org/${encodeURIComponent(
      guid
    )}?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// ─── Org SMS Templates ────────────────────────────────────────────────────────

export function getOrgSmsTemplates(organizationGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/sms_templates_org?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createOrgSmsTemplate(organizationGuid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/sms_templates_org?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function updateOrgSmsTemplate(guid, organizationGuid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/sms_templates_org/${encodeURIComponent(
      guid
    )}?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function deleteOrgSmsTemplate(guid, organizationGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/sms_templates_org/${encodeURIComponent(
      guid
    )}?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// ─── Org Webhook Templates ────────────────────────────────────────────────────

export function getOrgWebhookTemplates(organizationGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `api/webhooks_templates?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function createOrgWebhookTemplate(organizationGuid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `api/webhooks_templates?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function updateOrgWebhookTemplate(guid, organizationGuid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `api/webhooks_templates/${encodeURIComponent(
      guid
    )}?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function deleteOrgWebhookTemplate(guid, organizationGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `api/webhooks_templates/${encodeURIComponent(
      guid
    )}?organization_guid=${encodeURIComponent(
      organizationGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// ─── Commission Service ───────────────────────────────────────────────────────
//
// `partner` is a required query param on nearly every commission-service
// endpoint. It is injected in exactly ONE place: the crudApiInstance request
// interceptor (addPartnerQuery, top of this file). No path below spells it out
// and no caller ever passes it. (Verified: axios 0.15.3 combines baseURL before
// request interceptors run, so the interceptor sees an absolute URL. It also
// leaves an explicit `partner` alone, so overriding stays possible.)
//
// These 40-odd endpoints previously repeated the same four lines of
// auth-header-then-request boilerplate each. They now share one helper.

// Sends commission-service calls to a service running on this machine instead
// of the deployed one. Off unless explicitly set, ignored in production
// builds, and scoped to these endpoints — crudApiInstance's baseURL is the
// engine host shared with auth, policies and everything else, so repointing it
// wholesale would take the rest of the app down with it.
//
// Set at runtime rather than only through the env var, because picking it up
// from the environment needs a dev-server restart, and a restart sends the app
// through LandingPage, whose componentWillMount calls localStorage.clear() and
// logs the operator out:
//
//   window.__COMMISSION_SERVICE_BASE_URL__ = 'http://localhost:4000/'
function csConfig(config) {
  if (process.env.NODE_ENV === 'production') return config
  const override =
    (typeof window !== 'undefined' && window.__COMMISSION_SERVICE_BASE_URL__) ||
    process.env.REACT_APP_COMMISSION_SERVICE_URL
  return override ? Object.assign({}, config, { baseURL: override }) : config
}

// Arrays go out as repeated keys (agent_guids=a&agent_guids=b). axios 0.15
// writes agent_guids[]=a, which the commission service does not parse — it
// drops the filter and returns every row.
export function csSerializeParams(params) {
  const parts = []
  const add = (key, value) => {
    if (value === undefined || value === null) return
    const text = value instanceof Date ? value.toISOString() : String(value)
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(text)}`)
  }
  Object.keys(params || {}).forEach(key => {
    const value = params[key]
    if (Array.isArray(value)) value.forEach(item => add(key, item))
    else add(key, value)
  })
  return parts.join('&')
}

function csRequest(method, path, options) {
  // Auth is attached the same way as every other request in this file.
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  const opts = options || {}
  const config = csConfig(
    opts.params
      ? { params: opts.params, paramsSerializer: csSerializeParams }
      : {}
  )
  if (method === 'get' || method === 'delete') {
    return crudApiInstance[method](path, config)
  }
  return crudApiInstance[method](path, opts.payload || {}, config)
}

const csGet = (path, params) => csRequest('get', path, { params })
const csPost = (path, payload, params) =>
  csRequest('post', path, { payload, params })
const csPut = (path, payload, params) =>
  csRequest('put', path, { payload, params })
const csDelete = path => csRequest('delete', path)

// Path-segment encoder, so a guid containing odd characters cannot break a URL.
const seg = value => encodeURIComponent(value)

// Agents
export function getCommissionAgents(params) {
  return csGet('api/agents', params)
}

export function getCommissionAgent(guid) {
  return csGet(`api/agents/guid/${seg(guid)}`)
}

// Used by the "create agent from a platform user" flow to disable users that
// are already commission agents.
export function commissionAgentExists(guid) {
  return csGet(`api/agents/${seg(guid)}/exists`)
}

export function getCommissionAgentByNationalId(nationalId) {
  return csGet(`api/agents/national-id/${seg(nationalId)}`)
}

export function getCommissionAgentByExternalId(externalIdentifier) {
  return csGet(`api/agents/external/${seg(externalIdentifier)}`)
}

export function createCommissionAgent(payload) {
  return csPost('api/agents', payload)
}

export function updateCommissionAgent(guid, payload) {
  return csPut(`api/agents/${seg(guid)}`, payload)
}

// Soft delete — marks the agent inactive.
export function deleteCommissionAgent(guid) {
  return csDelete(`api/agents/${seg(guid)}`)
}

// Hard delete — irreversible. Gate this behind a typed confirmation.
export function deleteCommissionAgentPermanent(guid) {
  return csDelete(`api/agents/${seg(guid)}/permanent`)
}

// Agencies
//
// An agency is independent of any agent: it has no platform user, so its guid is
// generated client-side at create time. The service exposes no delete, so an
// agency is create/list/view/edit only.
export function getCommissionAgencies(params) {
  return csGet('api/agencies', params)
}

export function getCommissionAgency(guid) {
  return csGet(`api/agencies/guid/${seg(guid)}`)
}

export function createCommissionAgency(payload) {
  return csPost('api/agencies', payload)
}

export function updateCommissionAgency(guid, payload) {
  return csPut(`api/agencies/${seg(guid)}`, payload)
}

// Agent banks
//
// NOTE: the general list requires `swift_code` as a mandatory query param,
// which makes it unusable for browsing all banks (Gap 2 in
// docs/commissions-rewrite-step0.md). Prefer getAgentBanksByAgent.
export function getAgentBanks(params) {
  return csGet('api/agents/banks', params)
}

export function getAgentBanksByAgent(agentGuid) {
  return csGet(`api/agents/banks/agent/${seg(agentGuid)}`)
}

export function getAgentBank(guid) {
  return csGet(`api/agents/banks/${seg(guid)}`)
}

export function createAgentBank(payload) {
  return csPost('api/agents/banks', payload)
}

export function updateAgentBank(guid, payload) {
  return csPut(`api/agents/banks/${seg(guid)}`, payload)
}

export function deleteAgentBank(guid) {
  return csDelete(`api/agents/banks/${seg(guid)}`)
}

export function deleteAgentBankPermanent(guid) {
  return csDelete(`api/agents/banks/${seg(guid)}/permanent`)
}

// Commission rules
//
// NOTE: the list requires both `broker_company` and `upfront`, so it is always
// a scoped query (Gap 3). There is no DELETE endpoint for rules (Gap 8).
export function getCommissionRules(params) {
  return csGet('api/commissions/rules', params)
}

export function getCommissionRule(guid) {
  return csGet(`api/commissions/rules/${seg(guid)}`)
}

export function createCommissionRule(payload) {
  return csPost('api/commissions/rules', payload)
}

export function updateCommissionRule(guid, payload) {
  return csPut(`api/commissions/rules/${seg(guid)}`, payload)
}

// Body is { symbols, formulas } — flat, unlike the clawback equivalent.
// Results come back in cents.
export function validateCommissionRule(payload) {
  return csPost('api/commissions/rules/validate', payload)
}

// Clawback rules
export function getClawbackRules(params) {
  return csGet('api/clawbacks/rules', params)
}

export function getClawbackRule(guid) {
  return csGet(`api/clawbacks/rules/${seg(guid)}`)
}

export function createClawbackRule(payload) {
  return csPost('api/clawbacks/rules', payload)
}

export function updateClawbackRule(guid, payload) {
  return csPut(`api/clawbacks/rules/${seg(guid)}`, payload)
}

// Body is { policy_clawback_formulas, symbols, sample_symbols_values } —
// nested in period blocks, unlike the commission equivalent.
export function validateClawbackRule(payload) {
  return csPost('api/clawbacks/rules/validate', payload)
}

// Symbols produced by commission rules (e.g. gross_commission_amount), offered
// as autocomplete in clawback formulas.
export function getClawbackRuleSymbols() {
  return csGet('api/clawbacks/rules/symbols/commission-rules')
}

// Transactions — three families with the same shape.
export function getCommissionTransactions(params) {
  return csGet('api/commissions/transactions', params)
}

export function getCommissionTransactionByGuid(guid) {
  return csGet(`api/commissions/transactions/${seg(guid)}`)
}

export function getCommissionTransactionsByAgent(agentGuid, params) {
  return csGet(`api/commissions/transactions/agent/${seg(agentGuid)}`, params)
}

// Stats accept optional from_date / to_date.
export function getCommissionTransactionStats(params) {
  return csGet('api/commissions/transactions/statistics/summary', params)
}

export function getClawbackTransactions(params) {
  return csGet('api/clawbacks/transactions', params)
}

export function getClawbackTransactionByGuid(guid) {
  return csGet(`api/clawbacks/transactions/${seg(guid)}`)
}

export function getClawbackTransactionsByAgent(agentGuid, params) {
  return csGet(`api/clawbacks/transactions/agent/${seg(agentGuid)}`, params)
}

export function getClawbackTransactionStats(params) {
  return csGet('api/clawbacks/transactions/statistics/summary', params)
}

// NOTE: unlike the other two lists, this one requires `policy_type` and does
// not accept policy_guid / policy_number / product_code (Gap 4).
export function getPayoutTransactions(params) {
  return csGet('api/payout/transactions', params)
}

export function getPayoutTransactionByGuid(guid) {
  return csGet(`api/payout/transactions/${seg(guid)}`)
}

export function getPayoutTransactionsByAgent(agentGuid, params) {
  return csGet(`api/payout/transactions/agent/${seg(agentGuid)}`, params)
}

export function getPayoutTransactionStats(params) {
  return csGet('api/payout/transactions/statistics/summary', params)
}

// Commission accounts (agent ledger)
export function getCommissionAccountByGuid(guid) {
  return csGet(`api/commission-accounts/${seg(guid)}`)
}

// Latest account transaction for an agent — treated as the current balance.
export function getAgentCommissionAccount(agentGuid) {
  return csGet(`api/commission-accounts/agent/${seg(agentGuid)}`)
}

// NOTE: this endpoint requires the agent guid in BOTH the path and the
// `agent_guids` query param (Gap 6), so the query is filled in here rather
// than leaving every caller to remember it.
export function getAgentCommissionAccountTransactions(agentGuid, params) {
  const merged = Object.assign({ agent_guids: [agentGuid] }, params || {})
  return csGet(
    `api/commission-accounts/agent/transactions/${seg(agentGuid)}`,
    merged
  )
}

// Cross-agent ledger — the entry point for building a payout batch.
export function getCommissionAccountsForAgents(params) {
  return csGet('api/commission-accounts/agents/transactions', params)
}

// Batch payouts
export function getBatchPayouts(params) {
  return csGet('api/batch-payouts', params)
}

export function getBatchPayout(guid) {
  return csGet(`api/batch-payouts/${seg(guid)}`)
}

// 409 when an agent's account is already in an open batch.
export function createBatchPayout(payload) {
  return csPost('api/batch-payouts', payload)
}

// Moves money. The service treats a replayed idempotency key as the same
// request, so a retry must reuse the key rather than mint a new one. 409 when
// the batch is not payable (already being paid, or not Pending/Failed_Payment).
export function payBatchPayouts(batchPayoutGuid, idempotencyKey) {
  return csPost(
    'api/batch-payouts/pay',
    { idempotency_key: idempotencyKey },
    { batch_payout_guid: batchPayoutGuid }
  )
}

// Body: { effected_at, transaction_reference, feedback }, all required.
export function confirmBatchPayout(batchPayoutGuid, body) {
  return csPost('api/batch-payouts/confirm-payment', body, {
    batch_payout_guid: batchPayoutGuid
  })
}

// Always 200: an unsaved partner gets the defaults.
export function getCommissionSettings() {
  return csGet('api/commissions/settings')
}

export function updateCommissionSettings(payload) {
  return csPut('api/commissions/settings', payload)
}

// Every calculation the service attempted, including the ones that produced
// no transaction (Not_Eligible, No_Premium_Received, ...).
export function getCommissionCalculationLog(params) {
  return csGet('api/commissions/transaction-requests', params)
}

export function getEngineFlows(partner) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/engine/flows?partner=${encodeURIComponent(partner)}`
  )
}

export function getEngineFlowRaw(partner, flowId) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/engine/flows/${encodeURIComponent(
      flowId
    )}/raw?partner=${encodeURIComponent(partner)}`
  )
}

export function publishEngineFlow(partner, flowId, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/engine/flows/${encodeURIComponent(
      flowId
    )}?partner=${encodeURIComponent(partner)}`,
    { yaml: rawYaml }
  )
}

export function createEngineFlow(partner, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/engine/flows?partner=${encodeURIComponent(partner)}`,
    { yaml: rawYaml }
  )
}

export function getTaxConfigs(partner) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/kyc/tax/configs?partner=${encodeURIComponent(partner)}`
  )
}

export function getTaxConfigRaw(partner, type) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/kyc/tax/configs/${encodeURIComponent(partner)}/${type}/raw`
  )
}

export function publishTaxConfig(partner, type, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/kyc/tax/configs/${encodeURIComponent(partner)}/${type}`,
    { yaml: rawYaml }
  )
}

export function createTaxConfig(partner, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/kyc/tax/configs?partner=${encodeURIComponent(partner)}`,
    { yaml: rawYaml }
  )
}

export function getEngineConfigs(partner) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    partner
      ? `/api/kyc/v2/configs?partner=${encodeURIComponent(partner)}`
      : `/api/kyc/v2/configs`
  )
}

export function getEngineConfigRaw(partner, process, identifierType) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/kyc/v2/configs/${encodeURIComponent(
      partner
    )}/${process}/${identifierType}/raw`
  )
}

export function publishEngineConfig(partner, process, identifierType, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/kyc/v2/configs/${encodeURIComponent(
      partner
    )}/${process}/${identifierType}`,
    { yaml: rawYaml }
  )
}

export function createEngineConfig(partner, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/kyc/v2/configs?partner=${encodeURIComponent(partner)}`,
    { yaml: rawYaml }
  )
}

export function getIprsConfigs(partner) {
  return crudApiInstance.get(
    `/api/kyc/iprs/configs?partner=${encodeURIComponent(partner)}`,
    { headers: { Authorization: `Bearer ${getAuthorizationToken()}` } }
  )
}

export function getIprsConfigRaw(partner, type) {
  return crudApiInstance.get(
    `/api/kyc/iprs/configs/${encodeURIComponent(partner)}/${type}/raw`,
    { headers: { Authorization: `Bearer ${getAuthorizationToken()}` } }
  )
}

export function publishIprsConfig(partner, type, rawYaml) {
  return crudApiInstance.put(
    `/api/kyc/iprs/configs/${encodeURIComponent(partner)}/${type}`,
    { yaml: rawYaml },
    { headers: { Authorization: `Bearer ${getAuthorizationToken()}` } }
  )
}

export function createIprsConfig(partner, rawYaml) {
  return crudApiInstance.post(
    `/api/kyc/iprs/configs?partner=${encodeURIComponent(partner)}`,
    { yaml: rawYaml },
    { headers: { Authorization: `Bearer ${getAuthorizationToken()}` } }
  )
}

export function getAmlConfigs(partner) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/kyc/aml/configs?partner=${encodeURIComponent(partner)}`
  )
}

export function getAmlConfigRaw(partner, type) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/kyc/aml/configs/${encodeURIComponent(partner)}/${type}/raw`
  )
}

export function publishAmlConfig(partner, type, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/kyc/aml/configs/${encodeURIComponent(partner)}/${type}`,
    { yaml: rawYaml }
  )
}

export function createAmlConfig(partner, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/kyc/aml/configs?partner=${encodeURIComponent(partner)}`,
    { yaml: rawYaml }
  )
}

export function getRiskConfigs(partner) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/kyc/risk/configs?partner=${encodeURIComponent(partner)}`
  )
}

export function getRiskConfigRaw(partner) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/kyc/risk/configs/${encodeURIComponent(partner)}/raw`
  )
}

export function publishRiskConfig(partner, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/kyc/risk/configs/${encodeURIComponent(partner)}`,
    { yaml: rawYaml }
  )
}

export function createRiskConfig(partner, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/kyc/risk/configs?partner=${encodeURIComponent(partner)}`,
    { yaml: rawYaml }
  )
}

export function getGatewayConfigs(partner) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/payment_requests/gateway/configs?partner=${encodeURIComponent(
      partner
    )}`
  )
}

export function getGatewayConfigRaw(partner, id) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/payment_requests/gateway/configs/${encodeURIComponent(
      id
    )}/raw?partner=${encodeURIComponent(partner)}`
  )
}

export function publishGatewayConfig(partner, id, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/payment_requests/gateway/configs/${encodeURIComponent(
      id
    )}?partner=${encodeURIComponent(partner)}`,
    { yaml: rawYaml }
  )
}

export function createGatewayConfig(partner, rawYaml) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/payment_requests/gateway/configs?partner=${encodeURIComponent(
      partner
    )}`,
    { yaml: rawYaml }
  )
}

export function getOmkGroupsList(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  let params = `partner=${encodeURIComponent(
    getPartnerGuid()
  )}&page=${args.page || 1}&limit=${args.limit || 10}`
  if (args.status) {
    params += `&status=${args.status}`
  }
  if (args.group_name) {
    params += `&group_name=${args.group_name}`
  }
  if (args.group_type) {
    params += `&group_type=${args.group_type}`
  }
  if (args.group_category) {
    params += `&group_category=${args.group_category}`
  }
  return crudApiInstance.get(`/api/groups?${params}`)
}

export function getOmkGroup(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/${encodeURIComponent(guid)}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`
  )
}

export function approveOmkGroup(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/groups/${encodeURIComponent(
      guid
    )}/approve?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

// "Initiate bulk policy onboarding for all group members" — answers with the
// number of jobs queued and the number skipped. Approving a group only moves
// it to Endorsed; issuing the members' policies is this separate call.
export function onboardOmkGroupPolicies(guid, body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/groups/${encodeURIComponent(
      guid
    )}/policies/onboard?partner=${encodeURIComponent(getPartnerGuid())}`,
    body || {}
  )
}

// Answers with the whole group; the field that matters is
// `policies_registered`, which flips true once every member's policy exists.
// Onboarding is asynchronous (it queues jobs), so this is what tells the UI
// when to stop waiting and re-read the list.
export function getOmkGroupPolicyStatus(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/${encodeURIComponent(
      guid
    )}/policy_status?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function setOmkGroupVerification(guid, verificationType, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/groups/${encodeURIComponent(
      guid
    )}/verifications/${verificationType}?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    payload
  )
}

// Group registration documents follow the group service's presigned-POST flow:
//   1. getOmkGroupUploadUrl(guid, filename) → { signedUrl, bucketKey, fields }
//   2. postOmkGroupDocumentToS3(...)        → multipart POST straight to S3
//   3. addOmkGroupDocument(guid, { name, url: bucketKey })
// Every step-3/delete response is the whole updated group, so the caller can
// drop it straight back into the store instead of refetching.

// The upload_url endpoint rejects spaces and most punctuation ("Invalid
// filename"), so normalise before asking for a key.
export function sanitizeUploadFilename(name) {
  const raw = name || ''
  const dot = raw.lastIndexOf('.')
  const base =
    (dot > 0 ? raw.slice(0, dot) : raw)
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '') || 'document'
  const ext = (dot > 0 ? raw.slice(dot + 1) : '')
    .replace(/[^A-Za-z0-9]+/g, '')
    .toLowerCase()
  return ext ? `${base}.${ext}` : base
}

export function getOmkGroupUploadUrl(guid, filename) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/${encodeURIComponent(
      guid
    )}/upload_url?filename=${encodeURIComponent(
      sanitizeUploadFilename(filename)
    )}&partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function postOmkGroupDocumentToS3(signedUrl, fields, file) {
  // A presigned S3 POST is self-authenticating — it must NOT carry our bearer
  // token. Use a clean axios instance and strip any inherited Authorization
  // default so the token never leaks to S3.
  const s3 = axios.create()
  delete s3.defaults.headers.common.Authorization
  const form = new FormData()
  const policyFields = fields || {}
  Object.keys(policyFields).forEach(function(key) {
    form.append(key, policyFields[key])
  })
  // S3 ignores everything after the file part, so the file has to go last.
  form.append('file', file)
  // Let the browser set multipart/form-data with its own boundary.
  return s3.post(signedUrl, form, { transformRequest: [data => data] })
}

export function addOmkGroupDocument(guid, payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/groups/${encodeURIComponent(
      guid
    )}/documents?partner=${encodeURIComponent(getPartnerGuid())}`,
    payload
  )
}

export function deleteOmkGroupDocument(guid, docGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    `/api/groups/${encodeURIComponent(guid)}/documents/${encodeURIComponent(
      docGuid
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function getGroupPayments(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  let params = `partner=${getPartnerGuid()}&page=${args.page ||
    1}&limit=${args.limit || 10}`
  if (args.status) {
    params += `&status=${args.status}`
  }
  if (args.group_guid) {
    params += `&group_guid=${args.group_guid}`
  }
  if (args.from_date) {
    params += `&from_date=${args.from_date}`
  }
  if (args.to_date) {
    params += `&to_date=${args.to_date}`
  }
  return crudApiInstance.get(`/api/groups/payments?${params}`)
}

export function getGroupPayment(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/groups/${args.group_guid}/payments/${
      args.payment_guid
    }?partner=${getPartnerGuid()}`
  )
}

export function updateGroupPaymentStatus(args) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/groups/${args.group_guid}/payments/${
      args.payment_guid
    }/status?partner=${getPartnerGuid()}`,
    { status: args.status, notes: args.notes }
  )
}

// ───────────────────────── Collections (recurring-payment) ─────────────────────────
// All routes live under /api/debit_orders/collections and are partner-scoped.

function collectionsQuery(params) {
  const parts = [`partner=${encodeURIComponent(getPartnerGuid())}`]
  Object.keys(params || {}).forEach(key => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    }
  })
  return parts.join('&')
}

function collectionsUrl(path, params) {
  return `api/debit_orders/collections${path}?${collectionsQuery(params)}`
}

export function getCollectionsSummary(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/summary', params))
}

export function getCollectionsSeries(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/series', params))
}

export function getCollectionsUpcoming(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/upcoming', params))
}

export function getCollectionsAttempts(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/attempts', params))
}

export function getCollectionsOrder(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    collectionsUrl(`/orders/${encodeURIComponent(guid)}`)
  )
}

export function previewCollectionsTrigger(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/trigger/preview'), body)
}

export function runCollectionsTrigger(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/trigger'), body)
}

export function runDueCollectionRetries() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/retries/run-due'), {})
}

export function getCollectionRuns(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/runs', params))
}

export function pauseCollectionOrder(guid, body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    collectionsUrl(`/orders/${encodeURIComponent(guid)}/pause`),
    body
  )
}

export function resumeCollectionOrder(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    collectionsUrl(`/orders/${encodeURIComponent(guid)}/resume`),
    {}
  )
}

export function getCollectionHolds(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/holds', params))
}

export function createCollectionHold(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/holds'), body)
}

export function createCollectionHoldsBulk(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/holds/bulk'), body)
}

export function releaseCollectionHold(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    collectionsUrl(`/holds/${encodeURIComponent(guid)}`)
  )
}

export function releaseCollectionHoldsBySource(source) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/holds/release-by-source'), {
    source
  })
}

// ───────────────────────── Collections segments ─────────────────────────
export function previewCollectionsSegment(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/segments/preview'), body)
}

export function getCollectionSegments() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/segments'))
}

export function createCollectionSegment(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/segments'), body)
}

export function updateCollectionSegment(guid, body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    collectionsUrl(`/segments/${encodeURIComponent(guid)}`),
    body
  )
}

export function deleteCollectionSegment(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    collectionsUrl(`/segments/${encodeURIComponent(guid)}`)
  )
}

// ───────────────────────── Scheduled collections ("promise to pay") ─────────────────────────
export function scheduleCollection(guid, body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    collectionsUrl(`/orders/${encodeURIComponent(guid)}/schedule`),
    body
  )
}

export function cancelScheduledCollection(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.delete(
    collectionsUrl(`/scheduled/${encodeURIComponent(guid)}`)
  )
}

export function getCollectionsByPolicy(policyGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    collectionsUrl(`/by-policy/${encodeURIComponent(policyGuid)}`)
  )
}

// ───────────────────────── Dashboard: attention, at risk, due retries ─────────────────────────
export function getCollectionsAttention() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/attention'))
}

export function getCollectionsAtRisk(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/at-risk', params))
}

export function getDueRetriesPreview() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/retries/due-preview'))
}

// ───────────────────────── Work allocation: a lead for every customer ─────────────────────────
export function previewAllocation(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/allocations/preview'), body)
}

export function allocateWork(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/allocations'), body)
}

export function reassignWork(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/allocations/reassign'), body)
}

export function releaseWork(body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(collectionsUrl('/allocations/release'), body)
}

export function getMyQueue(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/my/queue', params))
}

export function logContact(ownershipGuid, body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    collectionsUrl(`/owners/${encodeURIComponent(ownershipGuid)}/activity`),
    body
  )
}

export function getCollectionsTeam() {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(collectionsUrl('/team'))
}

export function getAllocationBatch(guid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    collectionsUrl(`/batches/${encodeURIComponent(guid)}`)
  )
}

// Payment Collections dashboard (payment-request): api/payment_requests/collections/*
function paymentCollectionsUrl(path, params) {
  const query = Object.keys(params || {})
    .filter(key => params[key] !== undefined && params[key] !== '')
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
  query.unshift(`partner=${encodeURIComponent(getPartnerGuid())}`)
  return `api/payment_requests/collections${path}?${query.join('&')}`
}

export function getPaymentCollectionsOverview(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(paymentCollectionsUrl('/overview', params))
}

export function getPaymentCollectionsTimeline(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(paymentCollectionsUrl('/timeline', params))
}

export function getPaymentCollectionsFailures(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(paymentCollectionsUrl('/failures', params))
}

export function getPaymentCollectionsHourly(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(paymentCollectionsUrl('/hourly', params))
}

export function getPaymentCollectionsTransactions(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(paymentCollectionsUrl('/transactions', params))
}

export function exportPaymentCollectionsCsv(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    paymentCollectionsUrl('/transactions/export', params),
    { responseType: 'blob' }
  )
}

// Refunds desk (policy-administration-complex): every refund of the partner with its status, one policy's
// premiums and what can be done with each, refunding a premium, sending a failed refund again, and the
// refunds held for approval. The partner is always the selected partner.
function refundsUrl(path, params) {
  const query = Object.keys(params || {})
    .filter(
      key =>
        params[key] !== undefined && params[key] !== null && params[key] !== ''
    )
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
  query.unshift(`partner=${encodeURIComponent(getPartnerGuid())}`)
  return `api/policies/complex/${path}?${query.join('&')}`
}

export function getRefunds(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(refundsUrl('refunds', params))
}

export function getRefundPolicy(policyNumber) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    refundsUrl('refunds/policy', { policy_number: policyNumber })
  )
}

export function refundPremium({ policy_guid, premium_guid, reason }) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(refundsUrl('premiums/refund'), {
    policy_guid,
    premium_guid,
    reason
  })
}

export function retryRefund(refundGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    refundsUrl(`refunds/${encodeURIComponent(refundGuid)}/retry`),
    {}
  )
}

export function getPremiumApprovals(params) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(refundsUrl('premium-approvals', params))
}

export function decidePremiumApproval(guid, decision, reason) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    refundsUrl(`premium-approvals/${encodeURIComponent(guid)}/decide`),
    { decision, reason }
  )
}

// ───────────────────────── Emple agent onboarding ─────────────────────────
// KYC lookup by phone (kycservice), the Emplelife Plus onboarding rule on the engine, and the payment/consent status.

export function getKycCustomerByMsisdn(msisdn) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/kyc/customer?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&msisdn=${encodeURIComponent(msisdn)}`
  )
}

export function getAgentByExternalId(code) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/agents/external/${encodeURIComponent(
      code
    )}?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}

export function onboardEmplifePlus(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/engine/run/emplife_plus_agent?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    payload
  )
}

// The payment / consent requests recorded against a policy, newest first in the list the service returns.
export function listPolicyPaymentRequests(policyGuid) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/payment_requests?partner=${encodeURIComponent(
      getPartnerGuid()
    )}&policy_guid=${encodeURIComponent(policyGuid)}`
  )
}

// Changes the debit day (and first debit date) on a debit order that already exists for the policy.
export function updateDebitOrderDay(policyGuid, body) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/debit_orders/policy?policy_guid=${encodeURIComponent(
      policyGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    body
  )
}

// Sends a fresh payment / consent request (the agent's "send the prompt again").
// Switches an existing Emplife Plus policy to another package (the engine rule builds and swaps the quotation).
export function changeEmpleCover(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/engine/run/emplife_plus_change_cover?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    payload
  )
}

// Changes the amount a policy's debit order collects each month (the debit order is found by policy).
export function updateDebitOrderAmount(policyGuid, amountInCents) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.put(
    `/api/debit_orders/policy?policy_guid=${encodeURIComponent(
      policyGuid
    )}&partner=${encodeURIComponent(getPartnerGuid())}`,
    { amount_in_cents: amountInCents }
  )
}

export function resendPaymentRequest(payload) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.post(
    `/api/payment_requests/direct?partner=${encodeURIComponent(
      getPartnerGuid()
    )}`,
    payload
  )
}

export function getPaymentRequestStatus(reference) {
  axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
  return crudApiInstance.get(
    `/api/payment_requests/${encodeURIComponent(
      reference
    )}/status?partner=${encodeURIComponent(getPartnerGuid())}`
  )
}
